// Sydney, "Jacaranda night on the harbour". A late-spring (November) night in a harbourside park by Farm Cove.
// Back to front: an indigo sky with the Southern Cross and a crescent moon; a distant storm out toward the Heads,
// flickering with sheet lightning; North Sydney and the CBD with Sydney Tower; the Harbour Bridge (arch lights,
// traffic, a train now and then, climbers' head torches); the harbour with ferries, a water taxi and a channel
// marker; the floodlit Opera House on Bennelong Point, its forecourt and promenade running back to the Botanic
// Garden's shore; our park: sandstone seawall, a lamp, a woman eating fish and chips on the bench (a gull hoping for
// one), a couple on the wall and an angler; and a blooming jacaranda framing the top and the left, with a possum,
// flying foxes and falling blossoms at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, makeHalo, drawHalo, text, textWidth, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 buffer, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const HXC = {};
const HX = h => HXC[h] || (HXC[h] = hex(h));          // cached #rrggbb -> [r, g, b]

/* ================= Scene constants ================= */
const HZ = 160;                                       // far waterline: Circular Quay, the bridge feet, North Sydney
const SEA = 226;                                      // top of our sandstone seawall
const MOON = { x: 326, y: 28, r: 8 };
const FOCUS = 226;                                    // the Opera House (portrait crop centre)
// Harbour Bridge: south pylons [s0, s1), the arch lands on the north pair at n0, which ends at n1 (see PYLONS)
const BR = { s0: 262, s1: 277, n0: 451, n1: 469, ax: 364, half: 87, uTop: 92, uEnd: 122, lTop: 99, lEnd: 157, deck: 132, pTop: 110 };
// the granite pylons [x0, w, top, fully floodlit, panel inset]: the south pair seen as one block; the north pair as
// the far (west) tower, where the arch lands, and the near (east) one, a 2 px gap between them for the deck
const PYLONS = [[BR.s0, BR.s1 - BR.s0, BR.pTop, true, 5], [450, 7, BR.pTop + 1, false, 9], [459, 10, BR.pTop, true, 4]];
const OH = { x0: 146, x1: 300, top: 168, base: 182 };   // Opera House podium on Bennelong Point
// Bennelong Point: the podium stands on a promenade (rows OH.base..SHORE - 3) above a sandstone seawall, which runs
// on to the point's north tip (PT); the forecourt under the Monumental Steps and the Botanic Garden's shore fill the
// land from the frame's left edge to LAND_X1, so the harbour only shows in front of the point and beyond its tip
const SHORE = 187, PT = 306, LAND_X1 = 168;
const LAMP = { x: 92, y: 183 };                       // park lamp (lantern centre)
const SYT = { x: 150, top: 30, pod: 50 };             // Sydney Tower
const BB = { x: 94, y: 116, w: 46, h: 20 };           // the billboard's face: a screen on the roof of the Quay's station

/* ================= Painting helpers ================= */
function painter(w = W, h = H) {                      // ImageData painter: put(x, y, '#rrggbb'), has(x, y)
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const put = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const o = (y * w + i) * 4, rgb = HX(col);
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = 255;
  };
  const has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  const done = () => { x.putImageData(img, 0, 0); return c; };
  return { c, x, put, has, done };
}
function dpick(pal, v, i, y) {                        // ordered-dithered pick from a palette ramp
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
function line(put, x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) put(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
}
/* crop a layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = L => ctx.drawImage(L.c, L.x, L.y);
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
function sprite(rows, pal, flip = false) {           // rows of palette keys ('.' = transparent)
  const w = rows[0].length, [c, x] = makeCanvas(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (pal[row[i]]) px(x, flip ? w - 1 - i : i, j, pal[row[i]]); });
  return c;
}

/* ================= Sky: indigo overhead, violet city glow low on the left ================= */
const SKY_P = ['#07091c', '#090c22', '#0c1028', '#0f142f', '#131936', '#171e3d', '#1b2344', '#20294b', '#262f51', '#2c3557',
  '#333b5c', '#3b4260', '#434863'];
const SKY_WARM = ['#07091c', '#090c22', '#0c1028', '#10132f', '#161735', '#1d1a3b', '#251e41', '#2f2347', '#3a284c', '#462e51',
  '#523455', '#5f3b58', '#6c435a'];
function renderSky() {
  const p = painter(W, HZ + 2);
  for (let y = 0; y < HZ + 2; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / HZ), 1.3);
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 110, (y - MOON.y) / 70)) * 0.16;          // moonlit haze
    const warm = smooth(350, 50, i) * smooth(40, 150, y) * 0.95                               // the CBD's glow
      + smooth(150, 230, i) * smooth(300, 240, i) * smooth(96, 158, y) * 0.35;                // floodlit Opera House
    p.put(i, y, dpick(warm > bayer(i + 1, y + 2) ? SKY_WARM : SKY_P, v, i, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(61);
  while (STARS.length < 120) {
    const x = Math.floor(r() * W), y = Math.floor(Math.pow(r(), 1.4) * 118);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    const c = r();
    STARS.push({ x, y, k: 11 + Math.floor(r() * 70), ph: r(), b: 0.22 + r() * 0.55, big: r() < 0.05,
      col: c < 0.7 ? '#e4eaff' : c < 0.85 ? '#fff0d8' : '#d8d0ff' });
  }
  // the Southern Cross and the Pointers, high over the storm
  const cx = 420, cy = 12;
  for (const [dx, dy, b] of [[0, 0, 0.9], [1, 15, 1], [-7, 7, 0.95], [6, 5, 0.75], [3, 10, 0.5], [-30, 18, 1], [-21, 14, 0.9]])
    STARS.push({ x: cx + dx, y: cy + dy, k: 17 + ((dx * 7 + dy * 3) & 31), ph: (dx + 40) / 97, b, big: b > 0.85, col: dx === 0 && dy === 0 ? '#ffe0c8' : '#eef0ff' });
}
function renderMoon() {                               // waxing crescent, lit on the left (southern hemisphere evening)
  const r = MOON.r, s = r * 2 + 1, [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    const lit = Math.hypot(dx - 4.4, dy + 1.4) > r - 0.3;
    if (lit) px(x, i, y, dd > r - 1 ? '#e6dcb4' : dx < -4 ? '#fff8e0' : '#f2eac6');
    else px(x, i, y, dd > r - 1 ? '#23273f' : '#1a1e35');                                   // earthshine
  }
  return c;
}

/* ================= A distant storm out toward the Heads (sheet lightning) ================= */
const STORM = { x0: 290, y0: 40, w: 190, h: 120 };
const STORM_PUFFS = [];
{
  const r = rng(808), add = (x, y, rx, ry) => STORM_PUFFS.push({ x, y, rx, ry });
  for (let i = 0; i < 10; i++) { const v = i / 9; add(418 + (r() - 0.5) * 20 - v * 4, 150 - v * 78, 20 - v * 6 + r() * 6, 10 + r() * 4); }   // the tower
  for (let i = 0; i < 12; i++) add(418 + (r() < 0.5 ? -1 : 1) * (13 + r() * 9), 142 - r() * 66, 6 + r() * 5, 5 + r() * 3);             // cauliflower flanks
  for (let i = 0; i < 15; i++) {                                                                                                 // the anvil
    const x = 338 + i * 10.5 + r() * 5, e = clamp(1 - Math.abs(x - 424) / 92, 0, 1);
    add(x, 61 + (1 - e) * 3 + r() * 2, 11 + r() * 9, 2.5 + e * 4.5 + r() * 1.2);
  }
  for (let i = 0; i < 6; i++) { const v = i / 5; add(354 + (r() - 0.5) * 12, 152 - v * 40, 14 + r() * 6 - v * 3, 7 + r() * 3); }
  for (let i = 0; i < 7; i++) { const v = i / 6; add(470 + (r() - 0.5) * 10, 152 - v * 56, 15 + r() * 6 - v * 3, 8 + r() * 3); }
  for (let i = 0; i < 8; i++) add(304 + i * 24 + r() * 10, 149 + r() * 4, 20 + r() * 12, 6 + r() * 3);                           // low shelf
}
function stormField() {                               // density, plus a "which way does this bump face" term for cauliflower shading
  const { x0, y0, w, h } = STORM, D = new Float32Array(w * h), S = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const gx = x0 + i, gy = y0 + y;
    let best = -9, sh = 0;
    for (const p of STORM_PUFFS) {
      const dx = (gx - p.x) / p.rx, dy = (gy - p.y) / p.ry, v = 1 - dx * dx - dy * dy;
      if (v > best) { best = v; sh = -(dx * 0.55 + dy * 0.85); }
    }
    D[y * w + i] = best + 0.06 * Math.sin(gx * 0.43 + gy * 0.21) * Math.sin(gx * 0.11 - gy * 0.37);
    S[y * w + i] = clamp(sh, -1, 1);
  }
  return { D, S };
}
const STORM_RAMP = ['#12162c', '#171b35', '#1c213e', '#222847', '#293052', '#323a5e', '#3d466c', '#4a547c'];
const LIT_P = ['#262852', '#363670', '#4e4c90', '#706ab2', '#9890d0', '#c4bae8', '#ece6ff'];
const FLASH_C = [{ x: 410, y: 96, r: 56, bx: 386, by: 58 }, { x: 452, y: 82, r: 48, bx: 420, by: 56 }, { x: 358, y: 116, r: 46, bx: 322, by: 54 }];
function renderStorm(F, lit) {                        // lit = 0: the dark cloud; 1..3: lit from inside around FLASH_C[lit - 1]
  const { x0, y0, w, h } = STORM, { D, S } = F, p = painter(w, h), th = 0.02;
  const at = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? -9 : D[y * w + i];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = D[y * w + i];
    if (v <= th) continue;
    const gy = y0 + y, s = S[y * w + i];
    const rim = at(i, y - 1) <= th || at(i - 1, y) <= th, rim2 = at(i, y - 2) <= th, bot = at(i, y + 1) <= th;
    if (!lit) {
      let q = 0.36 + 0.26 * s - smooth(96, 152, gy) * 0.3 + smooth(90, 50, gy) * 0.12;
      if (rim) q += 0.34; else if (rim2) q += 0.16; else if (bot) q -= 0.1;
      p.put(i, y, dpick(STORM_RAMP, q, i, y));
      continue;
    }
    const f = FLASH_C[lit - 1], gx = x0 + i;
    const L = Math.pow(clamp(1 - Math.hypot((gx - f.x) / f.r, (gy - f.y) / (f.r * 0.75)), 0, 1), 1.1);
    let q = 0.1 + L * (0.62 + 0.28 * s) + 0.1 * s;
    if (rim) q += 0.12; else if (bot) q -= 0.08;
    p.put(i, y, dpick(LIT_P, q, i, y));
  }
  return p.done();
}
function renderBolt(seed, down) {                     // spider lightning crawling under the anvil, or a far strike to the ground
  const w = down ? 16 : 70, h = down ? 30 : 30, p = painter(w, h), r = rng(seed), core = [];
  const walk = (x, y, dir, n, depth) => {
    for (let k = 0; k < n; k++) {
      const nx = down ? clamp(x + Math.floor(r() * 5) - 2, 1, w - 2) : x + dir * (1 + Math.floor(r() * 3));
      const ny = down ? y + 2 + Math.floor(r() * 2) : clamp(y + (depth ? Math.floor(r() * 3) : Math.floor(r() * 3) - 1), 1, h - 2);
      const m = Math.max(Math.abs(nx - x), Math.abs(ny - y));
      for (let j = 1; j <= m; j++) core.push([Math.round(lerp(x, nx, j / m)), Math.round(lerp(y, ny, j / m))]);
      if (depth < 2 && r() < 0.2) walk(nx, ny, r() < 0.5 ? dir : -dir, 2 + Math.floor(r() * 4), depth + 1);
      x = nx; y = ny;
      if (ny >= h - 2) break;
    }
  };
  if (down) walk(8, 0, 1, 20, 1); else { walk(35, 8, 1, 16, 0); walk(35, 8, -1, 15, 0); }
  for (const [x, y] of core) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) p.put(x + dx, y + dy, '#7a72c8');
  for (const [x, y] of core) p.put(x, y, hash(x, y, 3) < 0.7 ? '#ffffff' : '#dcd6ff');
  return p.done();
}
const FLASHES = [
  { t: 32.4, c: 1, big: true }, { t: 71.8, c: 2, big: false }, { t: 104.6, c: 3, big: true },
  { t: 139.2, c: 2, big: true }, { t: 176.5, c: 1, big: false }, { t: 211.9, c: 3, big: true },
];
const PULSES = { big: [[0, 0.7], [0.14, 0.35], [0.3, 1], [0.62, 0.55], [0.92, 0.25]], small: [[0, 0.45], [0.22, 0.3]] };
function flashAt(t) {                                 // the active flash: intensity 0..1, which one, time into it
  for (const f of FLASHES) {
    const dt = t - f.t;
    if (dt < 0 || dt > 1.8) continue;
    let I = 0;
    for (const [o, a] of f.big ? PULSES.big : PULSES.small) { const d = dt - o; if (d >= 0) I = Math.max(I, a * Math.exp(-d * 11)); }
    return { I, f, dt };
  }
  return null;
}

/* ================= North Sydney across the water (behind the bridge) ================= */
const NS_TOWERS = [[298, 6, 136], [306, 8, 131], [316, 5, 139], [384, 7, 122], [392, 9, 117], [402, 6, 127], [410, 10, 121],
  [421, 7, 129], [429, 8, 135], [438, 6, 131], [470, 9, 128]];
const nsTop = x => Math.round(144 + 2.5 * Math.sin(x * 0.05 + 1) + 1.5 * Math.sin(x * 0.17) + smooth(300, 262, x) * 5);
function renderNorth() {
  const p = painter(W, HZ), r = rng(91), wins = [], lights = [];
  for (const [tx, tw, top] of NS_TOWERS) {
    for (let y = top; y < HZ; y++) for (let i = tx; i < tx + tw; i++) {
      const fr = tx + tw - 1 - i;
      p.put(i, y, y === top ? '#353d60' : fr < 2 ? '#191f34' : i === tx ? '#2a3250' : '#212842');
    }
    for (let wy = top + 2; wy < HZ - 4; wy += 2) for (let wx = tx + 1; wx < tx + tw - 1; wx += 2) {
      const v = r();
      if (v < 0.32) wins.push({ id: wins.length, x: wx, y: wy, tog: v < 0.13, n: 2 + Math.floor(r() * 6), p: 0.5, off: r(),
        col: r() < 0.6 ? '#7e98c8' : '#c8a470' });
    }
  }
  for (let i = 262; i < W; i++) {
    const top = nsTop(i);
    for (let y = top; y < HZ; y++) {
      const dd = y - top;
      let col = hash(i >> 1, y >> 1, 5) < 0.3 ? '#101524' : '#13192a';
      if (dd === 0) col = '#242b45'; else if (dd === 1 && bayer(i, y) < 0.5) col = '#1b2137';
      if (y >= HZ - 2) col = y === HZ - 1 ? '#2a2a40' : '#1c1e30';                         // the waterfront
      p.put(i, y, col);
      if (dd > 1 && y < HZ - 2 && r() < 0.045) lights.push({ id: lights.length, x: i, y, tw: r() < 0.35, k: 15 + Math.floor(r() * 60), ph: r(),
        col: r() < 0.8 ? (r() < 0.5 ? '#d8a864' : '#f0c88a') : '#98b8f0' });
    }
    if (i % 5 === 0) lights.push({ id: lights.length, x: i, y: HZ - 2, tw: false, col: i % 3 ? '#ffcf88' : '#f0e0c0' });
  }
  const c = p.done();
  for (const w of wins) if (!w.tog) px(p.x, w.x, w.y, w.col);
  for (const l of lights) if (!l.tw) px(p.x, l.x, l.y, l.col);
  return { L: trim(c), wins: wins.filter(w => w.tog), lights: lights.filter(l => l.tw),
    beacons: [[392 + 4, 116], [410 + 5, 120]] };
}

/* ================= The CBD, Circular Quay and Sydney Tower ================= */
const CBD = [];
{
  const r = rng(133);
  let bx = 10;
  while (bx < 258) {
    const w = 6 + Math.floor(r() * 10);
    const prof = 0.3 + 0.7 * Math.exp(-(((bx - 140) / 78) ** 2));
    let h = Math.round(20 + prof * 80 * (0.55 + 0.45 * r()));
    if (Math.abs(bx + w / 2 - SYT.x) < 14) h = Math.min(h, 70);                              // keep Sydney Tower clear
    if (bx > 222) h = Math.min(h, 34);
    CBD.push({ id: CBD.length, x: bx, w, top: 150 - h, h, tone: Math.floor(r() * 4), style: Math.floor(r() * 3),
      crown: r(), cool: r() < 0.55, seed: Math.floor(r() * 1e6) });
    bx += w + (r() < 0.25 ? 1 : 0) - (r() < 0.3 ? 2 : 0);
  }
  CBD.sort((a, b) => a.h < b.h ? 1 : -1);                                                  // tall ones behind
}
const TONES = [['#171b2e', '#22273f', '#2e344e'], ['#161a2a', '#1f2438', '#2a3046'], ['#1a1c32', '#25283f', '#31354e'], ['#15192a', '#1e2336', '#282e44']];
function renderCity() {
  const p = painter(W, HZ), wins = [], beacons = [], crowns = [];
  for (const b of CBD) {
    const r = rng(b.seed), T = TONES[b.tone], set = b.crown > 0.72 && b.h > 58 ? 3 : 0;   // some towers set back near the top
    for (let y = b.top; y < 152; y++) for (let i = b.x; i < b.x + b.w; i++) {
      const inset = y < b.top + set * 3 ? set : 0;
      if (i < b.x + inset || i >= b.x + b.w - inset) continue;
      const fr = b.x + b.w - inset - 1 - i;
      let col = fr < 2 ? T[0] : i === b.x + inset ? T[2] : T[1];
      if (y === b.top || (inset === 0 && set && y === b.top + set * 3)) col = '#3a4062';
      if (y > 138 && bayer(i, y) < (y - 138) / 16) col = fr < 2 ? '#2a2234' : '#3a2c3c';     // street glow
      p.put(i, y, col);
    }
    const warm = ['#ffd48a', '#ffc070', '#ffe2a8'], cool = ['#cfe0ff', '#a8c4f0', '#e6eeff'];
    const pal = b.cool ? cool : warm;
    const lit = (x, y, pr) => {
      const v = r();
      if (v >= pr) return;
      wins.push({ id: wins.length, x, y, tog: v < pr * 0.35, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.4, off: r(),
        col: r() < 0.85 ? pal[Math.floor(r() * 3)] : (b.cool ? '#ffd48a' : '#cfe0ff') });
    };
    const x0 = b.x + 1, x1 = b.x + b.w - 2;
    if (b.style === 0) { for (let y = b.top + set * 3 + 2; y < 146; y += 2) for (let x = x0; x < x1; x += 2) lit(x, y, 0.34); }
    else if (b.style === 1) { for (let y = b.top + set * 3 + 2; y < 146; y += 3) for (let x = x0; x < x1; x++) if (hash(x >> 2, y, b.id) < 0.5) lit(x, y, 0.5); }
    else for (let y = b.top + set * 3 + 2; y < 146; y += 2) for (let x = x0 + 1; x < x1; x += 3) { lit(x, y, 0.3); }
    if (b.crown > 0.86 && b.h > 64) {                                                       // a mast with a red light
      const mx = b.x + (b.w >> 1);
      for (let y = b.top - 9; y < b.top; y++) p.put(mx, y, '#3a3e58');
      beacons.push([mx, b.top - 10]);
    } else if (b.crown > 0.6 && b.h > 50) crowns.push({ x: b.x + 1 + (set ? set : 0), y: b.top + 1, w: b.w - 2 - 2 * (set ? set : 0), col: b.crown > 0.75 ? '#ffe6b0' : '#9ef0e0' });
    else if (b.w > 8) { p.put(b.x + 2, b.top - 1, '#262a42'); p.put(b.x + 3, b.top - 1, '#262a42'); p.put(b.x + 2, b.top - 2, '#262a42'); }
    if (b.h > 72 && !beacons.some(([bx]) => bx >= b.x && bx < b.x + b.w)) beacons.push([b.x + (b.w >> 1), b.top - 1]);
  }
  // Sydney Tower: a slim shaft in a web of cables, the golden turret, the mast
  const X = SYT.x;
  for (let y = 64; y < 150; y++) { p.put(X - 1, y, '#8a8ca6'); p.put(X, y, '#5c5e78'); p.put(X + 1, y, '#3a3c54'); }
  for (let k = 0; k <= 80; k++) {
    const y = Math.round(lerp(148, 66, k / 80));
    if (k % 2) { p.put(Math.round(lerp(X - 7, X - 2, k / 80)), y, '#34374f'); p.put(Math.round(lerp(X + 7, X + 2, k / 80)), y, '#2c2f46'); }
  }
  // the turret in straight bands: a narrower top section under a flat cap, the wide golden drum (floors of gold glass,
  // the restaurant windows glowing), a flat underside where the cables meet the shaft
  const GOLD = ['#6a4818', '#a87428', '#d8a040', '#f4cc66', '#fff0b0'];
  for (let y = SYT.pod; y <= 63; y++) {
    const hw = y < 54 ? 3 : y < 62 ? 5 : y === 62 ? 3 : 2;
    for (let dx = -hw; dx <= hw; dx++) {
      let v = 0.74 - (dx + hw) / (2 * hw) * 0.6;
      if (y === SYT.pod || y === 54) v += 0.24;                                            // the flat cap and the drum's lit top rim
      else if (y === 52 || y === 53) v -= y === 52 ? 0.34 : 0.1;                           // a dark glazed band round the top section
      else if (y === 56 || y === 58 || y === 60) v -= 0.34;                                // floors of gold glass
      let col = dpick(GOLD, v, X + dx, y);
      if (y === 57 && Math.abs(dx) < hw) col = dx < 2 ? '#fff0c4' : '#e8c88a';            // restaurant windows
      if (y >= 62) col = dx < 0 ? '#6a4818' : '#4a3212';                                   // the underside, in its own shadow
      if (Math.abs(dx) === hw && y > 54 && y < 62) col = dx < 0 ? dpick(GOLD, v + 0.1, X + dx, y) : '#5a3c14';   // crisp straight sides
      p.put(X + dx, y, col);
    }
  }
  for (let y = SYT.top; y < SYT.pod; y++) p.put(X, y, y < 40 ? '#6a6c86' : '#8a8ca6');
  for (const [dx, y] of [[-1, 44], [1, 44], [-1, 47], [1, 47], [-2, 48], [2, 48]]) p.put(X + dx, y, '#5c5e78');
  beacons.push([X, SYT.top - 1]);
  // Circular Quay: the station and expressway, the Rocks under the bridge, the promenade lights
  for (let i = 0; i < 262; i++) {
    for (let y = 150; y < HZ; y++) {
      let col = y === 150 ? '#2e2c40' : y === 151 ? '#1a1826' : bayer(i, y) < 0.5 ? '#1c1a28' : '#201c2c';
      if (y === 158) col = '#3a3040'; else if (y === HZ - 1) col = '#4a3a3a';
      p.put(i, y, col);
    }
  }
  for (let i = 222; i < 262; i++) {                                                        // the Rocks: sandstone terraces
    const top = 141 + Math.round(2 * Math.sin(i * 0.4)) + (i % 9 < 2 ? 2 : 0);
    for (let y = top; y < 150; y++) p.put(i, y, y === top ? '#4a3a3a' : (i % 9 === 8 ? '#1c1822' : '#2e2630'));
  }
  const c = p.done(), x = p.x;
  for (const w of wins) if (!w.tog) px(x, w.x, w.y, w.col);
  for (let i = 2; i < 262; i += 3) {
    if (hash(i, 1, 7) < 0.55) px(x, i, 153, hash(i, 2, 7) < 0.7 ? '#ffc878' : '#e8d8b8');
    if (hash(i, 3, 7) < 0.4) px(x, i, 155, '#d89858');
  }
  for (let i = 1; i < 262; i += 6) { px(x, i, 149, '#ffd898'); px(x, i + 3, 157, '#ffe6b0'); }
  for (let i = 224; i < 262; i += 3) if (hash(i, 4, 7) < 0.6) px(x, i, 144 + (i % 2) * 2, '#ffbe6a');
  return { L: trim(c), wins: wins.filter(w => w.tog), beacons, crowns };
}

/* ================= The billboard: a screen on the roof of the Quay's station (the city's one billboard) =================
   A slim screen cabinet on a braced steel trestle that stands on the station roof, a grated catwalk under the
   screen and a service ladder, all lit by the screen itself. The face (BB) shows the city's own ad, an invented
   surf school's sunset poster (a surfer riding in front of a striped sun); promo captures show the pitch. */
const BB_ST = { d: '#08090f', m: '#0f1019', h: '#2a2e46', s: '#454a6a', w: '#62546a', w2: '#433a50' };   // steel shades, screen-lit
function renderBillboard() {
  const p = painter(W, H), put = p.put;
  const L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 2, B = BB.y + BB.h + 1;   // cabinet box
  const CW = B + 1, BOT = 149;                                                   // catwalk row; the station roof is below 149
  const posts = [L + 2, L + 13, L + 25, L + 36, R - 2];
  for (let k = 0; k + 1 < posts.length; k++) {                                   // X braces between the trestle posts
    const a = posts[k] + 1, b = posts[k + 1] - 1;
    line(put, a, CW + 3, b, BOT - 1, BB_ST.m); line(put, a, BOT - 1, b, CW + 3, BB_ST.m);
  }
  posts.forEach((x, k) => {
    for (let y = CW + 2; y <= BOT; y++) put(x, y, BB_ST.d);
    if (k === 0) for (let y = CW + 2; y <= BOT; y++) put(x - 1, y, BB_ST.h);
  });
  for (let i = L; i <= R; i++) put(i, BOT, i % 2 ? BB_ST.m : BB_ST.d);           // base rail on the roof
  for (let i = L - 1; i <= R; i++) { put(i, CW, i % 2 ? BB_ST.w : BB_ST.w2); put(i, CW + 1, BB_ST.d); }   // grated catwalk
  for (let y = CW; y <= BOT; y++) {                                              // service ladder at the left end
    put(L - 3, y, BB_ST.m); put(L - 1, y, BB_ST.m);
    if (y % 2 === 0) put(L - 2, y, BB_ST.h);
  }
  for (let y = T; y <= B; y++) for (let i = L; i <= R; i++)                      // the cabinet and its black bezel
    put(i, y, y === T ? BB_ST.s : i === L ? BB_ST.h : i === R || y === B ? BB_ST.d : '#07080e');
  return p.done();
}
// the city's own ad: SURF / LESSONS beside a striped sunset sun, a surfer riding in front of it (drawn per frame)
const AD_SKY = ['#3a1f5c', '#56246a', '#7c2c70', '#a8386e', '#d0506a', '#ec7060', '#fc9458', '#ffba62'];
const AD_SEA = ['#6ad6cc', '#34aab2', '#1e869c', '#176886', '#114c6c'];
const AD_SUN = ['#fff2b8', '#fff2b8', '#fff2b8', null, '#ffd274', null, '#ffac5a'];   // flat retro bands (rows 5..11), gaps show the sky
const AD_HZ = 12, AD_SUNX = 9, AD_SUNR = 7.6;               // horizon row, the sun's centre column and radius (face-local)
const AD_TXT = { x: 18, y: 2 };
const SURF_ROWS = [                                         // chunky 2 px lettering, 6x8 per letter
  '.1111..11..11.11111..111111',
  '11..11.11..11.11..11.11....',
  '11.....11..11.11..11.11....',
  '.1111..11..11.11111..11111.',
  '....11.11..11.1111...11....',
  '....11.11..11.11.11..11....',
  '11..11.11..11.11..11.11....',
  '.1111...1111..11..11.11....',
];
const AD_WAVE = ['.WWW....', 'WlllWW..', 'lmmmllW.', 'mmdmmlW.', 'mmddmmlW', 'mmmddmml'];   // the curl behind the surfer, rows 6..11
const AD_WAVE_C = { W: '#f4fbff', l: '#8ae4d8', m: '#34aab2', d: '#1e7890' };
const SURFER = ['.....k...', '.kk.kkk..', 'k..kk..k.', '...kkk...', '..kk.kk..', '.kk...kk.', 'bbbbbbbbb'];   // riding right, arms out
function renderAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let col;
    if (j < AD_HZ) {
      col = dpick(AD_SKY, (j + 0.5) / AD_HZ, i, j);
      const band = AD_SUN[j - 5];
      if (band && Math.hypot(i - AD_SUNX, j - AD_HZ + 0.5) < AD_SUNR) col = band;
    } else col = j === AD_HZ ? (Math.abs(i - AD_SUNX) < 8 ? '#fff0b8' : '#9ae8dc') : dpick(AD_SEA, (j - AD_HZ) / (h - 1 - AD_HZ), i, j);
    put(i, j, col);
  }
  AD_WAVE.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') put(i, 6 + j, AD_WAVE_C[row[i]]); });
  SURF_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') put(AD_TXT.x + i + 1, AD_TXT.y + j + 1, '#2a1238'); });
  SURF_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') put(AD_TXT.x + i, AD_TXT.y + j, j < 2 ? '#fffaf0' : '#fff0d4'); });
  const c = p.done(), s = 'LESSONS';
  text(p.x, s, AD_TXT.x + Math.round((SURF_ROWS[0].length - textWidth(s)) / 2), 14, '#f2fbff');
  return c;
}
function drawAd(t) {
  const X = BB.x, Y = BB.y, s = step(t, 1200);
  ctx.drawImage(AD_C, X, Y);
  ctx.fillStyle = '#ffe6a0';                                                    // the sun's path glittering on the sea
  for (let j = AD_HZ + 1; j < BB.h; j++) {
    if (hash(j, 1, s) > 0.6) continue;
    const len = 1 + Math.floor(hash(j, 2, s) * 3), cx = AD_SUNX + Math.round((hash(j, 3, s) - 0.5) * (4 + (j - AD_HZ) * 0.8));
    ctx.fillRect(X + cx - (len >> 1), Y + j, len, 1);
  }
  ctx.fillStyle = '#f4fbff';                                                    // foam fizzing along the lip of the curl
  for (let k = 0; k < 6; k++) if (hash(k, s, 76) < 0.5) ctx.fillRect(X + Math.floor(hash(k, s, 75) * 7), Y + 5 + Math.floor(hash(k, s, 74) * 3), 1, 1);
  const sx = X + 5 + Math.round(1 + wave(t, 12)), sy = Y + AD_HZ - 7 + (wave(t, 48, 0.25) > 0.35 ? -1 : 0);   // carve and bob
  SURFER.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') px(ctx, sx + i, sy + j, row[i] === 'b' ? (i === 4 ? '#e8403a' : '#fff6e0') : '#2a1238'); });
  ctx.fillStyle = '#f4fbff';                                                    // spray kicked up behind the board
  for (let k = 0; k < 5; k++) if (hash(k, s, 77) < 0.65) ctx.fillRect(sx - 1 - Math.floor(hash(k, s, 78) * 3), sy + 6 - Math.floor(hash(k, s, 79) * 3), 1, 1);
  const sweep = frac(t * 24 / LOOP) * 6 - 1;                                    // a sheen sweeps across SURF every 10 s
  if (sweep > 0 && sweep < 1) {
    const bx = Math.round(lerp(-6, SURF_ROWS[0].length + 6, sweep));
    ctx.fillStyle = '#ffffff';
    SURF_ROWS.forEach((row, j) => { for (let d = 0; d < 2; d++) { const i = bx - j + d; if (row[i] === '1') ctx.fillRect(X + AD_TXT.x + i, Y + AD_TXT.y + j, 1, 1); } });
  }
}
function drawBillboard(t, pitch) {
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.85 + 0.1 * wave(t, 6, 0.3));
  blit(BILL_L);
  if (!PITCH.covers(t, pitch)) drawAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  if (frac(t / 2) < 0.5) { px(ctx, BB.x + BB.w, BB.y - 2, '#ff4a3a'); halo(HL.bbLed, BB.x + BB.w, BB.y - 2, 0.6); }   // a red beacon on the cabinet
}

/* ================= Sydney Harbour Bridge: steel through-arch between granite pylons ================= */
const uAt = x => (x - BR.ax) / BR.half;
const yU = x => BR.uTop + (BR.uEnd - BR.uTop) * uAt(x) ** 2;       // upper chord (top edge)
const yL = x => BR.lTop + (BR.lEnd - BR.lTop) * uAt(x) ** 2;       // lower chord (top edge)
const PANELS = [];
for (let k = -14; k <= 14; k++) PANELS.push(BR.ax + k * 6);
const ARCH_LIGHTS = [];
for (let x = BR.s1 + 5; x <= BR.n0 - 5; x += 5) ARCH_LIGHTS.push({ x, y: Math.round(yU(x)) - 1, k: 30 + (x % 23), ph: (x % 17) / 17 });
function renderBridge() {
  const body = painter(W, HZ), fr = painter(W, HZ);
  // deck and approach spans (behind the traffic)
  for (let i = 222; i < W; i++) {
    body.put(i, BR.deck, '#5a6282'); body.put(i, BR.deck + 1, '#2c3250'); body.put(i, BR.deck + 2, '#20253a'); body.put(i, BR.deck + 3, '#181c2e');
    const app = i < BR.s0 || i >= BR.n1;
    if (app) for (let y = BR.deck + 4; y < BR.deck + 8; y++) {                                 // approach girders
      const m = (i + (y - BR.deck) * 2) % 6;
      body.put(i, y, y === BR.deck + 7 ? '#2a3048' : m === 0 ? '#2e3450' : m === 3 ? '#262b42' : '#141828');
    }
  }
  for (const [px0, bot] of [[231, 141], [247, 142]]) for (let y = BR.deck + 8; y < bot; y++)
    for (let i = px0; i < px0 + 4; i++) body.put(i, y, i === px0 ? '#8a7c68' : i === px0 + 3 ? '#3e3830' : '#62584a');
  // the near arch rib: chords, truss web, hangers and posts (in front of the traffic)
  const put = fr.put;
  for (let i = BR.s1 - 3; i <= BR.n0 + 3; i++) {
    const u = Math.abs(uAt(i)), yu = Math.round(yU(i)), yl = Math.round(yL(i));
    const ylN = Math.round(yL(i + (i < BR.ax ? -1 : 1)));
    put(i, yu, '#7c86aa'); put(i, yu + 1, '#3c4462');
    if (u > 0.55) put(i, yu + 2, '#2a3048');
    for (let y = yl; y <= Math.max(yl, ylN - 1); y++) put(i, y, '#343a58');                 // fill steep steps
    put(i, yl + 1, '#5c668e');
  }
  PANELS.forEach((x, k) => {
    const top = Math.round(yU(x)) + 2, bot = Math.round(yL(x)) - 1;
    for (let y = top; y <= bot; y++) put(x, y, '#2e3450');
    if (k < PANELS.length - 1) {
      const x2 = PANELS[k + 1], toward = x2 <= BR.ax;
      if (toward) line(put, x, top, x2, Math.round(yL(x2)) - 1, '#262b44');
      else line(put, x2, Math.round(yU(x2)) + 2, x, bot, '#262b44');
    }
    const yl = Math.round(yL(x));
    if (yl + 2 < BR.deck) for (let y = yl + 2; y < BR.deck; y++) put(x, y, '#262b42');        // hangers
    else if (yl > BR.deck + 4) for (let y = BR.deck + 4; y < yl; y++) put(x, y, '#262b42');   // posts
  });
  for (let i = 222; i < W; i++) { put(i, BR.deck - 3, '#3c4462'); if (i % 3 === 0) { put(i, BR.deck - 2, '#2a3048'); put(i, BR.deck - 1, '#2a3048'); } }
  for (const L of ARCH_LIGHTS) put(L.x, L.y, '#fff0d0');
  for (const fx of [BR.ax - 2, BR.ax + 2]) for (let y = BR.uTop - 5; y < BR.uTop; y++) put(fx, y, '#5a6282');   // flagpoles
  // granite pylons, floodlit from below, with stepped crowns. The south pair overlaps into one block behind the
  // Opera House; the north pair reads as two towers, the far (west) one on the left, with the deck passing between
  for (const [p0, w, top, lit, inner] of PYLONS) {
    const st = w >= 15 ? [1, 3, 5] : w >= 10 ? [1, 2, 3] : [1, 2, w], sh = w >= 10 ? 3 : 2;   // crown setbacks, shaded columns
    for (let y = top - 6; y < HZ; y++) for (let i = p0 - 1; i <= p0 + w; i++) {
      const dx = i - p0;
      let inside = dx >= 0 && dx < w;
      if (y >= 147 && (dx === -1 || dx === w)) inside = true;
      if (y < top) { const k = top - y, s = st[(k - 1) >> 1]; inside = dx >= s && dx < w - s; }
      if (!inside) continue;
      const v = (y - top) / (HZ - top), fromR = w - 1 - dx;
      let col = dpick(['#7a6e5c', '#8e8270', '#a0927c', '#b2a488'], 0.15 + v * 0.85 - (lit ? 0 : 0.12), i, y);
      if ((y - top) % 6 === 5) col = '#7a6e5c';
      if (dx >= inner && dx < w - inner && y > 119 && y < 144) col = (y - top) % 6 === 5 ? '#665c4e' : '#7e7262';
      if (dx <= 0) col = lit ? '#d2c2a2' : '#b8a88c'; else if (dx === 1 && w >= 10) col = '#b8a88c';
      else if (fromR === 0) col = '#3a342e'; else if (fromR === 1) col = '#4a4238'; else if (fromR === 2 && sh > 2) col = '#5e5446';
      if (y === top || y === top - 3) col = fromR < sh ? '#8a7c68' : '#e2d2b2';
      else if (y === top + 1 || y === top - 2) col = fromR < sh ? '#5a5044' : '#9a8c74';
      if (y >= 147) col = y === 147 ? (fromR < 2 ? '#8a7c68' : '#d8c8a8') : dx <= 0 ? '#c8b898' : fromR < 3 ? '#4e463c' : '#9a8c76';
      put(i, y, col);
    }
    if (w >= 15) for (let dx = 3; dx < w - 3; dx += 2) { put(p0 + dx, top + 4, '#3a3228'); put(p0 + dx, top + 5, '#4a4034'); }
    else {                                                                                   // one tall window high on each face
      const a = (w - 1) >> 1, b = w >> 1;
      for (let y = top + 4; y <= top + 7; y++) for (let i = a; i <= b; i++) put(p0 + i, y, y === top + 7 ? '#4a4034' : '#3a3228');
    }
  }
  // between the north pair: open sky above the deck, the shadowed inner faces and the abutment below it
  const [fx0, fw] = PYLONS[1], gx0 = fx0 + fw, gx1 = PYLONS[2][0];
  for (let i = gx0; i < gx1; i++) for (let y = BR.deck + 4; y < 147; y++) put(i, y, y === BR.deck + 4 ? '#5a5044' : bayer(i, y) < 0.5 ? '#2e2a26' : '#3a342e');
  return { body: trim(body.done()), front: trim(fr.done()) };
}

/* ================= Bridge life: traffic, trains, climbers' head torches ================= */
const CARS = [];
{ const r = rng(211); for (let i = 0; i < 26; i++) CARS.push({ id: i, dir: i % 2 ? 1 : -1, n: 4 + Math.floor(r() * 3), ph: r() }); }
const TRAINS = [{ a: 36, b: 80, dir: 1 }, { a: 146, b: 190, dir: -1 }];
const TL = 66, TX0 = 188 - TL, TX1 = 492;                          // train length; it runs from behind the Rocks off the right edge
function trainX(tr, t) { const u = (t - tr.a) / (tr.b - tr.a); return Math.round(tr.dir > 0 ? lerp(TX0, TX1, u) : lerp(TX1, TX0, u)); }
function drawTraffic(t) {
  for (const c of CARS) {
    const { cyc, u } = cycle(t, c.n, c.ph);
    if (hash(c.id, cyc, 31) < 0.3) continue;
    const x = Math.round(c.dir > 0 ? lerp(210, 490, u) : lerp(490, 210, u));
    if (x < 222 || x > W) continue;
    const y = c.dir > 0 ? 131 : 130;
    rect(ctx, x - 1, y, 3, 1, '#23263a');
    const hx = x + c.dir, tx = x - c.dir;
    px(ctx, hx, y, '#fff4d0'); px(ctx, tx, y, '#ff4a3a');
    halo(HL.head, hx + c.dir, y, 0.55);
  }
  for (const tr of TRAINS) {
    if (t < tr.a || t >= tr.b) continue;
    const x0 = trainX(tr, t);
    ctx.save(); ctx.beginPath(); ctx.rect(222, 0, W - 222, H); ctx.clip();
    rect(ctx, x0, 125, TL, 1, '#6a7084'); rect(ctx, x0, 126, TL, 1, '#2a2e3a'); rect(ctx, x0, 127, TL, 1, '#a0a6b6');
    rect(ctx, x0, 128, TL, 1, '#2a2e3a'); rect(ctx, x0, 129, TL, 1, '#5a6072');
    ctx.fillStyle = '#ffe8b0';
    for (let i = 1; i < TL - 1; i++) if (i % 8 !== 0 && i % 3 !== 0) { ctx.fillRect(x0 + i, 126, 1, 1); if ((i + 1) % 3) ctx.fillRect(x0 + i, 128, 1, 1); }
    ctx.fillStyle = '#1a1c26';
    for (let i = 8; i < TL; i += 8) ctx.fillRect(x0 + i, 125, 1, 5);
    const head = tr.dir > 0 ? x0 + TL - 2 : x0;
    rect(ctx, head, 126, 2, 4, '#e8b030'); px(ctx, tr.dir > 0 ? head + 1 : head, 128, '#ffffff');
    px(ctx, tr.dir > 0 ? x0 : x0 + TL - 1, 128, '#ff3a30');
    halo(HL.trainHead, tr.dir > 0 ? head + 3 : head - 2, 128, 0.9);
    for (let k = 0; k < 3; k++) halo(HL.train, x0 + 11 + k * 22, 127, 0.55);
    const s = step(t, 2400);
    if (hash(s, 7, 99) < 0.05) {                                   // a pantograph spark
      const sx = x0 + 4 + 8 * Math.floor(hash(s, 8, 99) * 8);
      px(ctx, sx, 123, '#e8f4ff'); px(ctx, sx + 1, 122, '#a8c8ff'); halo(HL.spark, sx, 123, 1);
    }
    ctx.restore();
  }
}
function climberX(t) {                                             // the leader's x on the upper chord (null = off the arch)
  if (t < 20 || t >= 206) return null;
  if (t < 98) return lerp(BR.s1 + 2, BR.ax - 2, smooth(20, 98, t) * 0.3 + (t - 20) / 78 * 0.7);
  if (t < 124) return lerp(BR.ax - 2, BR.ax + 2, (t - 98) / 26);
  return lerp(BR.ax + 2, BR.n0 + 20, (t - 124) / 82);
}
function drawBridgeLights(t) {
  for (const L of ARCH_LIGHTS) halo(HL.archLight, L.x, L.y, 0.45 + 0.2 * wave(t, L.k, L.ph));
  const lx = climberX(t);
  if (lx !== null) for (let i = 0; i < 6; i++) {
    const x = Math.round(lx - i * 4);
    if (x <= BR.s1 || x >= BR.n0 - 1) continue;
    const y = Math.round(yU(x)) - 3, b = 0.7 + 0.3 * wave(t, 90 + i * 13, i * 0.2);
    px(ctx, x, y + 1, '#1a1e2c');                                                          // the climber, a torch on the head
    ctx.globalAlpha = b; px(ctx, x, y, '#bfeaff'); ctx.globalAlpha = 1;
    halo(HL.torch, x, y, 0.7 * b);
  }
  const f = Math.round(wave(t, 44)), f2 = Math.round(wave(t, 44, 0.3));   // the two flags at the crown
  rect(ctx, BR.ax - 1, BR.uTop - 5 + f, 1, 2, '#3a4a8a'); px(ctx, BR.ax, BR.uTop - 5 + f, '#2a3a7a');
  rect(ctx, BR.ax + 3, BR.uTop - 5 + f2, 2, 2, '#3a4a8a'); px(ctx, BR.ax + 3, BR.uTop - 5 + f2, '#d8dce8');
  if (frac(t / 2) < 0.45) { px(ctx, BR.ax, BR.uTop - 6, '#ff4040'); halo(HL.red, BR.ax, BR.uTop - 6, 0.9); }
  halo(HL.pylon, BR.s0 + 7, 150, 0.9); halo(HL.pylon, BR.n0 + 7, 150, 0.9);
}

/* ================= The harbour ================= */
const WATER_P = ['#2a4466', '#233c5e', '#1c3354', '#162a4a', '#11223e', '#0d1b34', '#0a152b'];
function renderWater() {
  const h = SEA - HZ, p = painter(W, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(y / h, 0.7);
    v -= Math.max(0, 1 - Math.abs(i - MOON.x) / 70) * 0.12 * (1 - v);
    p.put(i, y, dpick(WATER_P, v, i, y));
  }
  return p.done();
}
const WSTREAKS = [
  { x: SYT.x, w: 3, col: '#ffc860', a: 0.26, seed: 1 }, { x: 20, w: 2, col: '#ffc070', a: 0.2, seed: 2 },
  { x: 62, w: 2, col: '#ffd898', a: 0.18, seed: 3 }, { x: 104, w: 3, col: '#ffc070', a: 0.2, seed: 4 },
  { x: BR.s0 + 7, w: 5, col: '#e8c890', a: 0.3, seed: 5 }, { x: 453, w: 3, col: '#e8c890', a: 0.24, seed: 6 },
  { x: 464, w: 4, col: '#e8c890', a: 0.3, seed: 10 },
  { x: BR.ax, w: 9, col: '#fff0d0', a: 0.14, seed: 7 }, { x: 330, w: 2, col: '#d8b070', a: 0.16, seed: 8 },
  { x: 412, w: 2, col: '#98b8f0', a: 0.14, seed: 9 },
];
const RIPPLES = [];
{
  const r = rng(515);
  for (let i = 0; i < 56; i++) {
    const R = { id: i, x: Math.floor(r() * 460) + 10, y: HZ + 3 + Math.floor(Math.pow(r(), 0.8) * (SEA - HZ - 6)), w: 2 + Math.floor(r() * 5), n: 150 + Math.floor(r() * 120), ph: r() };
    if (R.y > SHORE || R.x > PT + 12) RIPPLES.push(R);                                     // none on the point
  }
}
function drawWater(t, fl) {
  ctx.drawImage(WATER_C, 0, HZ);
  blit(POINT_L);                                                                           // the garden's shore, mirrored below it
  PROM_LAMPS.forEach((x, i) => halo(HL.ohLamp, x, 175, 0.7 + 0.15 * wave(t, 600 + i * 29)));
  // the water off the garden mirrors from its seawall; beyond the point's tip, from the far shore (the Opera House
  // mirrors itself, see drawOperaHouse)
  for (const [top, x, w] of [[SHORE, 0, OH.x0], [HZ, PT + 1, W - PT - 1]])
    reflect(ctx, t, { top, rows: SEA - top, squash: 1.45, alpha: 0.44, k1: 40, k2: 96, amp: 0.5, grow: 0.05, x, w });
  rect(ctx, 0, SHORE, W, SEA - SHORE, 'rgba(6,12,32,0.2)'); rect(ctx, LAND_X1, HZ, W - LAND_X1, SHORE - HZ, 'rgba(6,12,32,0.2)');
  rect(ctx, LAND_X1, HZ, W - LAND_X1, 1, '#141a2e');
  for (const s of WSTREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.x < LAND_X1 ? SHORE : HZ + 1, SEA);
  for (const tr of TRAINS) if (t >= tr.a && t < tr.b) {
    const cx = trainX(tr, t) + TL / 2;
    if (cx > 230 && cx < W) streak(ctx, t, cx, 10, '#ffe0a0', 0.18, 11, HZ + 1, SEA);
  }
  streak(ctx, t, MOON.x, 7, '#b8c0e0', 0.28, 3.3, HZ + 1, SEA);                         // the moon path
  const s = step(t, 1440);
  for (let j = 0; j < 16; j++) {
    if (hash(j, s, 51) > 0.55) continue;
    const k = Math.floor(Math.pow(hash(j, s, 52), 0.8) * (SEA - HZ - 2)), spread = 2 + k * 0.2;
    ctx.globalAlpha = 0.85 - k / 110;
    px(ctx, Math.round(MOON.x + (hash(j, s, 53) - 0.5) * 2 * spread), HZ + 1 + k, hash(j, s, 54) < 0.5 ? '#ffffff' : '#f4ecc8');
  }
  ctx.fillStyle = '#6a86b8';
  for (const r of RIPPLES) {
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (hash(r.id, cyc, 58) < 0.4) continue;
    ctx.globalAlpha = Math.sin(Math.PI * u) * (0.22 + 0.3 * (r.y - HZ) / (SEA - HZ));
    ctx.fillRect(r.x + Math.round((hash(r.id, cyc, 59) - 0.5) * 24), r.y, r.w, 1);
  }
  ctx.globalAlpha = 1;
  if (fl && fl.I > 0.01) { const f = FLASH_C[fl.f.c - 1]; halo(HL.flashW, f.x, HZ + 24, fl.I * 0.8); }
}

/* ================= Ferries (green and cream, double-ended), a water taxi, channel markers ================= */
function makeFerry(big) {
  const w = big ? 50 : 30, h = big ? 15 : 11, p = painter(w, h), hullTop = h - 4, ld = hullTop - 3;
  for (let y = hullTop; y < h; y++) {
    const inset = y - hullTop >= 2 ? (y - hullTop - 1) * 2 : 0;
    for (let i = inset; i < w - inset; i++)
      p.put(i, y, y === hullTop ? '#d8d0b0' : y === h - 1 ? '#10241a' : ((i & 3) === 0 && y === hullTop + 1) ? '#23603e' : '#2c6e48');
  }
  for (let y = ld; y < hullTop; y++) for (let i = 2; i < w - 2; i++) {
    let col = y === ld ? '#fbf3dc' : '#eadfbe';
    if (y === ld + 1 && i > 3 && i < w - 4 && i % 3 !== 0) col = hash(i, w, 5) < 0.85 ? '#ffd890' : '#8a7a60';
    if (i === 2 || i === w - 3) col = '#b8ab88';
    p.put(i, y, col);
  }
  if (big) {
    const ud = ld - 3;
    for (let y = ud; y < ld; y++) for (let i = 5; i < w - 5; i++) {
      let col = y === ud ? '#fbf3dc' : '#e2d6b2';
      if (y === ud + 1 && i > 6 && i < w - 7 && i % 3 !== 1) col = hash(i, 9, 5) < 0.9 ? '#ffe2a0' : '#7a6a58';
      p.put(i, y, col);
    }
    for (const x0 of [6, w - 12]) for (let y = ud - 3; y < ud; y++) for (let i = x0; i < x0 + 6; i++)
      p.put(i, y, y === ud - 3 ? '#fbf3dc' : y === ud - 2 ? (i === x0 || i === x0 + 5 ? '#eadfbe' : '#2a3040') : '#eadfbe');
    for (let y = ud - 5; y < ud; y++) for (let i = 23; i < 27; i++) p.put(i, y, y < ud - 3 ? '#2c6e48' : i === 23 ? '#fbf3dc' : '#d8ccaa');
    for (const mx of [9, w - 10]) for (let y = 0; y < ud - 3; y++) p.put(mx, y, '#6a6e80');
  } else {
    for (let y = 1; y < ld; y++) for (let i = 10; i < 19; i++) p.put(i, y, y === 1 ? '#fbf3dc' : y === 2 && i > 10 && i < 18 ? '#2a3040' : '#eadfbe');
    for (let y = 0; y < 2; y++) for (let i = 20; i < 22; i++) p.put(i, y, '#2c6e48');
    p.put(20, 2, '#d8ccaa'); p.put(21, 2, '#d8ccaa'); p.put(20, 3, '#d8ccaa'); p.put(21, 3, '#d8ccaa');
  }
  return p.done();
}
const FERRIES = [
  { big: true, a: 16, b: 100, x0: -56, x1: 500, wl: 206, dir: 1 },         // a Manly ferry crossing in front
  { big: false, a: 118, b: 176, x0: 486, x1: 168, wl: 175, dir: -1 },     // into Circular Quay, behind the Opera House
  { big: false, a: 186, b: 234, x0: 168, x1: 492, wl: 175, dir: 1 },      // out again, heading for the Heads
];
const TAXIS = [{ a: 56, b: 88, x0: 494, x1: -20, wl: 217, dir: -1 }, { a: 150, b: 180, x0: -20, x1: 494, wl: 215, dir: 1 }];
function drawWake(x, w, wl, dir, big, t) {
  const stern = dir > 0 ? x + 2 : x + w - 3, bow = dir > 0 ? x + w - 2 : x + 1, len = big ? 70 : 46, s = step(t, 1200);
  ctx.fillStyle = '#e8f0ff';
  for (let d = 1; d < len; d++) {
    const xx = stern - dir * d;
    if (xx < 0 || xx >= W) continue;
    const fade = 1 - d / len, yn = wl + 1 + Math.round(d * (big ? 0.1 : 0.07));
    if (yn < SEA && hash(d, s, 41) < 0.7) { ctx.globalAlpha = 0.65 * fade; ctx.fillRect(xx, yn, 1, 1); }
    if (d < len * 0.6 && hash(d, s, 43) < 0.9 * fade) { ctx.globalAlpha = 0.85 * fade; ctx.fillRect(xx, wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
  }
  ctx.globalAlpha = 0.9;
  for (let k = 0; k < (big ? 6 : 4); k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + dir * (k >> 1), wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
}
function drawFerry(f, t) {
  if (t < f.a || t >= f.b) return;
  const spr = FERRY_SPR[f.big ? 0 : 1], w = spr.width, h = spr.height;
  const x = Math.round(lerp(f.x0, f.x1, (t - f.a) / (f.b - f.a))), y = f.wl - h + 1, rows = f.big ? 10 : 7;
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.34 * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, f.wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  drawWake(x, w, f.wl, f.dir, f.big, t);
  ctx.drawImage(spr, x, y);
  const cx = x + (w >> 1);
  for (const o of f.big ? [-14, 0, 14] : [-6, 6]) streak(ctx, t, cx + o, 2, '#ffd98a', 0.28, o + 7, f.wl + 2, Math.min(SEA, f.wl + 22));
  halo(HL.ferry, cx, y + (h >> 1), 0.65);
  for (const mx of f.big ? [9, w - 10] : [14]) { px(ctx, x + mx, y, '#ffffff'); halo(HL.mast, x + mx, y, 0.9); }
  const side = f.dir > 0 ? '#4aff7a' : '#ff4a3a', sx = f.dir > 0 ? x + w - (f.big ? 12 : 10) : x + (f.big ? 11 : 9);
  px(ctx, sx, f.wl - (f.big ? 9 : 6), side); halo(f.dir > 0 ? HL.green : HL.redL, sx, f.wl - (f.big ? 9 : 6), 0.7);
}
const TAXI_ROWS = ['.....wwww.....', '....wbbbbw....', '..yyyyyyyyyyy.', 'yyyyyyyyyyyyyy', 'kkkkkkkkkkkkk.', '.YYYYYYYYYYY..'];
const TAXI_PAL = { w: '#f4f2ea', b: '#ffe0a0', y: '#f0c030', Y: '#b88818', k: '#18181f' };
function drawTaxi(tx, t) {
  if (t < tx.a || t >= tx.b) return;
  const spr = TAXI_SPR[tx.dir > 0 ? 0 : 1], w = spr.width, h = spr.height;
  const x = Math.round(lerp(tx.x0, tx.x1, (t - tx.a) / (tx.b - tx.a))), y = tx.wl - h + 1, s = step(t, 1440);
  const stern = tx.dir > 0 ? x : x + w - 1, bow = tx.dir > 0 ? x + w : x - 1;
  for (let d = 1; d < 64; d++) {                                   // a long, spreading white V
    const xx = stern - tx.dir * d, fade = 1 - d / 64;
    if (xx < 0 || xx >= W) continue;
    ctx.fillStyle = '#eef6ff';
    if (hash(d, s, 61) < 0.9 * fade) { ctx.globalAlpha = 0.9 * fade; ctx.fillRect(xx, tx.wl, 1, 1); }
    const spread = Math.round(d * 0.12);
    if (hash(d, s, 62) < 0.6 * fade) { ctx.globalAlpha = 0.55 * fade; ctx.fillRect(xx, Math.min(SEA - 1, tx.wl + 1 + spread), 1, 1); }
    if (d < 20 && hash(d, s, 63) < 0.5) { ctx.globalAlpha = 0.6 * fade; ctx.fillRect(xx, tx.wl - 1, 1, 1); }
  }
  ctx.globalAlpha = 0.9; ctx.fillStyle = '#ffffff';
  for (let k = 0; k < 5; k++) if (hash(k, s, 64) < 0.75) ctx.fillRect(bow + tx.dir * (k % 3), tx.wl - 1 - (k >> 1), 1, 1);
  ctx.globalAlpha = 0.3;
  for (let j = 0; j < 4; j++) ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + Math.round(Math.sin(TAU * 60 * t / LOOP + j) * 0.8), tx.wl + 1 + j, w, 1);
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, y);
  halo(HL.taxi, x + (w >> 1), y + 1, 0.8);
  px(ctx, bow - tx.dir, y + 3, '#ffffff'); halo(HL.mast, bow - tx.dir, y + 3, 0.7);
  streak(ctx, t, x + (w >> 1), 3, '#ffe0a0', 0.3, 13, tx.wl + 2, SEA);
}
const MARKERS = [{ x: 432, y: 201, green: true, per: 3, on: 0.5 }, { x: 336, y: 172, green: false, per: 4, on: 0.6 }];
function drawMarkers(t) {
  for (const m of MARKERS) {
    const tall = m.green ? 6 : 4;
    rect(ctx, m.x, m.y - tall, 1, tall, '#1a1e2a'); rect(ctx, m.x - 1, m.y - tall - 1, 3, 1, m.green ? '#2a6a3a' : '#7a2a2a');
    px(ctx, m.x, m.y, '#2a3044');
    const on = frac(t / m.per + m.x / 97) < m.on / m.per;
    if (on) {
      px(ctx, m.x, m.y - tall - 2, m.green ? '#8aff9a' : '#ff6a5a');
      halo(m.green ? HL.green : HL.redL, m.x, m.y - tall - 2, 1);
      streak(ctx, t, m.x, 2, m.green ? '#5aff8a' : '#ff5a4a', 0.3, m.x, m.y + 1, Math.min(SEA, m.y + 20));
    }
  }
}

/* ================= Sydney Opera House: shell sails over glass foyers on a granite podium =================
   Each shell is a pointed sail: a rounded back rising from the back foot and curving over to the tip, and a
   concave mouth edge falling from the tip to the front foot. dir = +1 tips point north (right, toward the
   bridge), dir = -1 the back-to-back shell that faces the forecourt. g: 0 Concert Hall (behind), 1 Joan
   Sutherland Theatre (in front), 2 the restaurant shells. */
const SHELLS = [
  { g: 0, dir: -1, b: 216, f: 199, tx: 187, ty: 124, base: 166 },
  { g: 0, dir: 1, b: 213, f: 240, tx: 256, ty: 104, base: 166 },
  { g: 0, dir: 1, b: 236, f: 256, tx: 270, ty: 122, base: 166 },
  { g: 0, dir: 1, b: 254, f: 266, tx: 279, ty: 140, base: 166 },
  { g: 1, dir: -1, b: 236, f: 220, tx: 207, ty: 133, base: 168 },
  { g: 1, dir: 1, b: 233, f: 258, tx: 276, ty: 115, base: 168 },
  { g: 1, dir: 1, b: 256, f: 274, tx: 289, ty: 133, base: 168 },
  { g: 1, dir: 1, b: 272, f: 284, tx: 295, ty: 149, base: 168 },
  { g: 2, dir: -1, b: 182, f: 172, tx: 162, ty: 152, base: 168 },
  { g: 2, dir: 1, b: 180, f: 191, tx: 199, ty: 150, base: 168 },
];
function shellSpan(sh, y) {                           // [x0, x1, s] covered by the shell on row y (s: 0 base .. 1 tip), or null
  if (y < sh.ty || y > sh.base) return null;
  const s = (sh.base - y) / (sh.base - sh.ty);
  const back = sh.b + (sh.tx - sh.b) * (1 - Math.sqrt(Math.max(0, 1 - s * s)));
  const mouth = sh.f + (sh.tx - sh.f) * Math.pow(s, 1.6);
  return sh.dir > 0 ? [back, mouth, s] : [mouth, back, s];
}
// floodlit white tiles with a cool tint, shading to blue-grey; the brightest shade is kept off pure white so the
// sails keep their form, and only the back edge catching the light reaches it
const SHELL_P = [
  ['#5c6474', '#868ea0', '#aeb5c4', '#cbd0db', '#dfe2ea', '#eceef3'],                    // Concert Hall, set back
  ['#68707f', '#959dad', '#bec4d0', '#dadee6', '#ebeef2', '#fafbfc'],                    // Joan Sutherland, in front
  ['#646c7c', '#9098a8', '#b8bfcc', '#d5d9e2', '#e7eaef', '#f6f7f9'],
];
const flat = (pal, v) => pal[Math.round(clamp(v, 0, 1) * (pal.length - 1))];   // an undithered pick (mirror sources)
function renderOperaHouse(refl) {                   // refl: the undithered copy the harbour mirrors (tiles and mullions left out)
  const p = painter(W, HZ + 30), cover = new Int16Array(W).fill(999);
  for (const sh of SHELLS) for (let y = sh.ty; y <= sh.base; y++) {
    const sp = shellSpan(sh, y);
    for (let i = Math.round(sp[0]); i <= Math.round(sp[1]); i++) if (y < cover[i]) cover[i] = y;
  }
  // glass foyers glowing under the mouths of the shells
  const GLASS = ['#8a4a26', '#c07038', '#e89a4c', '#ffc878', '#ffe4a8'];
  for (let i = 150; i < 300; i++) {
    if (cover[i] > OH.top) continue;
    for (let y = cover[i]; y < OH.top; y++) {
      const v = (y - cover[i]) / Math.max(1, OH.top - cover[i]);
      let col = refl ? flat(GLASS, 0.5 + v * 0.5) : dpick(GLASS, 0.25 + v * 0.8, i, y);
      if (refl) { if (i % 6 === 0) col = '#9a5a2e'; }
      else if (i % 3 === 0) col = v > 0.5 ? '#9a5a2e' : '#6a3a20';
      else if ((y - OH.top) % 5 === 0) col = '#7a4428';
      p.put(i, y, col);
    }
  }
  for (const sh of SHELLS) {
    const P = SHELL_P[sh.g];
    for (let y = sh.ty; y <= sh.base; y++) {
      const [x0, x1, s] = shellSpan(sh, y);
      for (let i = Math.round(x0); i <= Math.round(x1); i++) {
        const q = sh.dir > 0 ? (i - x0) / Math.max(1, x1 - x0) : (x1 - i) / Math.max(1, x1 - x0);
        const toMouth = sh.dir > 0 ? x1 - i : i - x0, toBack = sh.dir > 0 ? i - x0 : x1 - i;
        let v = 0.86 - 0.32 * q - 0.16 * s;
        const gore = (q * 4) % 1;
        if (!refl && gore < 0.16 && q > 0.1) v -= 0.12;                                   // tile gores converging on the tip
        else if (!refl && ((sh.base - y) / 4 + Math.abs(gore - 0.58) * 1.2) % 1 < 0.25 && (i + y) % 3) v -= 0.2;   // chevrons of tile lids, pointing up
        if (toBack < 1) v = 1;
        let col = refl ? flat(P, v) : dpick(P, v, i, y);
        if (toMouth < 1.2 && s < 0.97) col = sh.g === 0 ? '#8a6e52' : '#a07c58';            // the ribbed underside at the mouth
        if (toMouth < 0.5 && s < 0.97) col = '#5a4232';
        p.put(i, y, col);
      }
    }
  }
  // podium with the monumental steps at the forecourt end (the mirror copy also carries the lamps along its foot); the
  // deck runs on under the restaurant shells (their tip is at x 162), and the steps fall away left of them
  const STEPS = 160;
  for (let i = OH.x0; i < OH.x1; i++) {
    const stepTop = i < STEPS ? OH.top + Math.round((STEPS - i) / 1.4) : OH.top;
    const round = i > OH.x1 - 4 ? i - (OH.x1 - 4) : 0;
    for (let y = stepTop + round; y < OH.base; y++) {
      let col;
      if (i < STEPS && y < OH.top + 11) col = ((y - OH.top) & 1) ? '#8a7466' : (y === stepTop ? '#e6d2ba' : '#cbb49c');
      else if (y === OH.top) col = '#ead6be';
      else if (y < OH.top + 3) col = '#c8b098';
      else if (y === OH.top + 7) col = '#7a6258';
      else if (y >= OH.base - 2) col = y === OH.base - 1 ? '#4a3c3c' : '#6a5654';
      else col = (i % 11 === 0) ? '#86706a' : refl ? '#a4897a' : bayer(i, y) < 0.5 ? '#a88e7c' : '#9c8272';
      if (refl && y >= OH.base - 4 && y <= OH.base - 2 && OH_LAMPS.includes(i)) col = y === OH.base - 3 ? '#fff0c0' : '#e8c08a';
      p.put(i, y, col);
    }
  }
  // the promenade and seawall under it, wrapping round the north tip
  for (let i = OH.x0; i <= PT; i++) for (let y = OH.base; y < SHORE; y++) {
    const end = PT - Math.max(0, SHORE - 2 - y);                                           // the tip's rounded corner
    if (i <= end) p.put(i, y, shoreCol(i, y, refl));
  }
  return p.done();
}
/* ================= Bennelong Point: the promenade, the forecourt and the Botanic Garden's shore =================
   The promenade's granite paving (three rows), the seawall's sandstone coping and its face down to the water; lit by
   the lamps at the podium's foot and along the garden's shore. Shared by the podium's layer and the garden's so the
   two meet without a seam. */
const PROM_LAMPS = [12, 32, 52, 72, 114, 132];         // heritage lamps along the garden's shore (our park lamp is at 92)
const PAVE = ['#26222c', '#342e36', '#463c40', '#5c4e4c', '#7a665a', '#9a8268'];
function shoreCol(i, y, refl) {
  let near = 9;
  for (const x of i < LAND_X1 ? PROM_LAMPS : OH_LAMPS) near = Math.min(near, Math.abs(i - x));
  const L = Math.max(0, 1 - near / 7);
  if (y < SHORE - 2) {                                                                     // paving, its far row in the wall's shadow
    const v = 0.36 + L * 0.5 + (y === OH.base && i >= OH.x0 ? -0.22 : 0) + (i < OH.x0 ? 0 : 0.06);
    if (!refl && y > OH.base && i % 8 === 3) return dpick(PAVE, v - 0.14, i, y);         // paving joints
    return refl ? flat(PAVE, v) : dpick(PAVE, v, i, y);
  }
  if (y === SHORE - 2) return refl ? flat(['#4a3e38', '#6e5c4c', '#8e7658'], 0.3 + L * 0.7) : dpick(['#4a3e38', '#6e5c4c', '#8e7658'], 0.3 + L * 0.7, i, y);   // coping
  return (i + 3) % 9 === 0 ? '#1a1618' : refl ? '#2e2628' : bayer(i, y) < 0.5 ? '#2a2326' : '#342a2a';                       // the wall's face
}
// Moreton Bay figs along the garden's shore, lower trees behind the forecourt: [x, y, rx, ry]
const FIGS = [];
{
  const r = rng(1717);
  for (let x = -4; x < 100; x += 11 + Math.floor(r() * 7)) FIGS.push([x, 171 + r() * 3, 10 + r() * 6, 11 + r() * 4]);
  for (let x = 106; x < LAND_X1 + 6; x += 10 + Math.floor(r() * 6)) FIGS.push([x, 169 + r() * 2, 7 + r() * 4, 10 + r() * 3]);
}
const FORE = 102;                                      // the forecourt, from here to the foot of the Monumental Steps
function renderPoint() {
  const p = painter(LAND_X1, SHORE);
  const FOL = ['#080e12', '#0b1316', '#0f181b', '#142023', '#1a292c', '#243638', '#30443f'];
  const FOL_W = ['#1c1a1e', '#2c2426', '#40322e', '#5a4436'];                              // floodlit by the Opera House
  const FORE_P = ['#2c262e', '#3a3036', '#4c4042', '#62524e', '#7c685e', '#957c6a'];
  for (let y = 148; y < SHORE; y++) for (let i = 0; i < LAND_X1; i++) {
    if (y >= OH.base) { p.put(i, y, shoreCol(i, y, false)); continue; }
    if (i >= FORE && y >= 176) {                                                           // the forecourt's paving
      let v = 0.2 + 0.5 * smooth(FORE, 150, i) + (y - 176) * 0.04;
      if (y === 176) v += 0.14;                                                            // its far kerb catching the light
      p.put(i, y, dpick(FORE_P, v, i, y));
      continue;
    }
    let best = -9, sx = 0, sy = 0;
    for (const [fx, fy, rx, ry] of FIGS) {
      const dx = (i - fx) / rx, dy = (y - fy) / ry, v = 1 - dx * dx - dy * dy;
      if (v > best) { best = v; sx = dx; sy = dy; }
    }
    best += (hash(i >> 1, y >> 1, 41) - 0.5) * 0.3;
    if (best > 0) {                                                                        // foliage: moonlit crowns, floodlit flanks
      const warm = smooth(80, 166, i) * smooth(0.3, 0.8, sx);
      let v = 0.38 - sy * 0.3 + (hash(i >> 1, y >> 1, 42) - 0.5) * 0.2;
      if (best < 0.2 && sy < -0.3) v += 0.3;
      p.put(i, y, warm > 0.25 + bayer(i, y) * 0.5 ? dpick(FOL_W, warm, i, y) : dpick(FOL, v, i, y));
      continue;
    }
    if (y < HZ) continue;                                                                  // the Quay shows over the crowns
    p.put(i, y, y > 178 ? dpick(['#0a1210', '#0e1814', '#14201a'], (y - 178) / 4, i, y) : bayer(i, y) < 0.5 ? '#090f12' : '#0b1215');   // lawns in shadow
  }
  for (const x of PROM_LAMPS) {                                                           // lamp posts; the lanterns glow per frame
    for (let y = 177; y < OH.base; y++) p.put(x, y, '#141418');
    p.put(x, 175, '#fff0c8'); p.put(x, 176, '#ffd890'); p.put(x - 1, 176, '#3a2e24'); p.put(x + 1, 176, '#3a2e24');
  }
  return p.done();
}
/* the harbour's mirror of the Opera House: the undithered copy flipped about the waterline, darkened and cooled
   toward the water so the sails read as upside-down shapes, their mouths still split by the dark ribbed edges */
function renderOperaMirror(src) {
  const top = Math.min(...SHELLS.map(s => s.ty)), w = PT + 1 - OH.x0, h = SHORE - top;
  const d = src.getContext('2d').getImageData(OH.x0, top, w, h).data, [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), o = img.data;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const a = ((h - 1 - j) * w + i) * 4, b = (j * w + i) * 4;
    if (!d[a + 3]) continue;
    o[b] = Math.round(d[a] * 0.86 + 4); o[b + 1] = Math.round(d[a + 1] * 0.84 + 8); o[b + 2] = Math.round(d[a + 2] * 0.8 + 20); o[b + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const OH_LAMPS = [];
for (let x = 176; x < OH.x1 - 4; x += 9) OH_LAMPS.push(x);
// strollers on the point's promenade, specks at this distance: each walks its length once a loop, out from behind the
// garden's trees or round the tip, where they pass out of sight
const STROLL = [{ ph: 0.08, dir: 1 }, { ph: 0.09, dir: 1, dx: 2 }, { ph: 0.47, dir: -1 }, { ph: 0.81, dir: 1 }];
function drawStrollers(t) {
  for (const s of STROLL) {
    const u = frac(t / LOOP + s.ph), x = Math.round(s.dir > 0 ? lerp(-4, PT, u) : lerp(PT, -4, u)) + (s.dx || 0);
    if (x >= PT - 1) continue;
    px(ctx, x, OH.base, '#c8a888'); px(ctx, x, OH.base + 1, '#16141a');                     // a lit head over a dark coat
  }
}
function drawOperaHouse(t) {
  halo(HL.oh, FOCUS, 146, 0.95);
  blit(OH_L);
  drawStrollers(t);
  for (const sh of SHELLS) if (sh.g !== 2) halo(HL.ohCore, sh.tx - sh.dir * 10, sh.base - 10, 0.16);   // overlapping: kept low
  OH_LAMPS.forEach((x, i) => { px(ctx, x, OH.base - 3, '#fff0c0'); halo(HL.ohLamp, x, OH.base - 3, 0.75 + 0.25 * wave(t, 700 + i * 37)); });
  // its mirror in the harbour, upside down: the seawall, the promenade, the podium and its lamps, then the foyers'
  // glow and the sails, squashed like the rest of the water's reflections and wobbling row by row
  const rows = SEA - SHORE, w = OH_R.width;
  for (let k = 0; k < rows; k++) {
    const sy = Math.floor(k * 1.45);
    if (sy >= OH_R.height) break;
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + k * 0.55) * (0.6 + k * 0.06) + Math.sin(TAU * 96 * t / LOOP - k * 1.3) * 0.8);
    ctx.globalAlpha = 0.72 * (1 - k / (rows + 24));
    ctx.drawImage(OH_R, 0, sy, w, 1, OH.x0 + off, SHORE + k, w, 1);
  }
  ctx.globalAlpha = 0.5;
  for (let y = SHORE + 3; y < SEA; y += 2) {                                              // dark ripple troughs drifting across it
    const n = y - SHORE;
    for (let j = 0; j < 2; j++) {
      const len = 3 + Math.floor(hash(n, j, 71) * (4 + n * 0.3)), x = OH.x0 + Math.round(frac(hash(n, j, 70) + (3 + j) * t / LOOP) * (w + 20)) - 10;
      rect(ctx, x, y, len, 1, '#0e1a34');
    }
  }
  ctx.globalAlpha = 1;
  for (const [x, w, a, seed] of [[206, 8, 0.14, 21], [250, 8, 0.16, 22]]) streak(ctx, t, x, w, '#ffe6c0', a, seed, SHORE, SEA);
  for (let i = 0; i < OH_LAMPS.length; i += 2) streak(ctx, t, OH_LAMPS[i], 2, '#ffd890', 0.3, 30 + i, SHORE, SHORE + 16);
}

/* ================= Our park: sandstone seawall, path, lawn carpeted with blossoms, bench and lamp ================= */
const lampLight = (x, y, rx = 90, ry = 44) => Math.max(0, 1 - Math.hypot((x - LAMP.x) / rx, (y - 236) / ry));
const BENCH = { x0: 104, x1: 136 };
function renderGround() {
  const p = painter(W, H), r = rng(717);
  const GRASS = ['#08120e', '#0c1712', '#101d16', '#15241b', '#1c2e20', '#263c26', '#34502e'];
  const PATH = ['#15121c', '#1c1924', '#24202c', '#2e2832', '#3e3436', '#54443c'];
  for (let i = 0; i < W; i++) {
    const L0 = lampLight(i, SEA);
    p.put(i, SEA, dpick(['#6a5848', '#8a7258', '#b09270', '#d4b48a'], 0.35 + L0 * 0.8, i, SEA));
    p.put(i, SEA + 1, (i + 7) % 14 === 0 ? '#3a2e28' : dpick(['#4a3c32', '#6a5646', '#8a7056'], 0.3 + L0 * 0.7, i, SEA + 1));
    p.put(i, SEA + 2, (i + 7) % 14 === 0 ? '#2a221e' : '#3a3030');
    for (let y = SEA + 3; y < 238; y++) {
      let v = 0.32 + lampLight(i, y) * 0.75 + (hash(i, y, 11) - 0.5) * 0.2;
      p.put(i, y, dpick(PATH, v, i, y));
    }
    p.put(i, 238, lampLight(i, 238) > 0.3 ? '#3a4032' : '#1e2a20');
    for (let y = 239; y < H; y++) {
      const depth = (y - 239) / (H - 239);
      let v = 0.36 - depth * 0.3 + lampLight(i, y, 100, 60) * 0.7;
      if (hash(i, y >> 1, 12) < 0.18) v += 0.12;                                           // tufts
      if (hash(i >> 1, y, 13) < 0.1) v -= 0.12;
      p.put(i, y, dpick(GRASS, v, i, y));
    }
  }
  // fallen jacaranda blossoms: thick under the tree, thinning toward the right
  const DRIFTS = [];
  for (let k = 0; k < 22; k++) DRIFTS.push({ x: Math.pow(r(), 1.3) * 400, y: 240 + r() * 32, rx: 12 + r() * 30, ry: 3 + r() * 5 });
  DRIFTS.push({ x: 60, y: 262, rx: 60, ry: 12 }, { x: 150, y: 232, rx: 70, ry: 3 });
  for (let y = 229; y < H; y++) for (let x = 0; x < W; x++) {
    if (y >= 236 && y <= 238) continue;
    let dens = 0;
    for (const d of DRIFTS) { const q = 1 - ((x - d.x) / d.rx) ** 2 - ((y - d.y) / d.ry) ** 2; if (q > dens) dens = q; }
    const pr = (0.03 + 0.85 * Math.pow(dens, 0.7)) * smooth(470, 120, x) * (y > 240 ? 1 : 0.6);
    if (hash(x >> 1, y, 14) * 0.6 + hash(x, y, 16) * 0.4 >= pr) continue;
    const L = lampLight(x, y, 100, 60), hv = hash(x, y, 15) + dens * 0.3;
    p.put(x, y, L > 0.4 ? (hv < 0.6 ? '#9a74d0' : '#c09ae8') : hv < 0.35 ? '#473274' : hv < 0.75 ? '#5a3e8e' : hv < 1 ? '#6e50a8' : '#8664c0');
  }
  // heritage lamp post: fluted post, lantern on top (the glass glows per frame)
  const X = LAMP.x;
  for (let y = 230; y < 238; y++) { const hw = y > 235 ? 3 : y > 232 ? 2 : 1; for (let dx = -hw; dx <= hw; dx++) p.put(X + dx, y, dx === -hw ? '#4a5448' : '#141c18'); }
  for (let y = 192; y < 230; y++) { p.put(X - 1, y, y < 206 ? '#8a7050' : '#3e4a42'); p.put(X, y, '#18221c'); p.put(X + 1, y, '#101612'); }
  for (const y of [198, 214, 226]) { p.put(X - 2, y, '#3e4a42'); p.put(X + 2, y, '#141c18'); }
  for (let y = 188; y < 192; y++) for (let dx = -2; dx <= 2; dx++) p.put(X + dx, y, y === 188 ? '#5a4a36' : '#141c18');
  for (let y = 176; y < 188; y++) for (let dx = -3; dx <= 3; dx++) {
    const edge = Math.abs(dx) === 3;
    p.put(X + dx, y, edge ? '#141c18' : dx === 0 && y > 178 ? '#e8b060' : y < 180 ? '#fff6dc' : y < 184 ? '#ffe2a0' : '#ffc870');
  }
  for (let y = 172; y < 176; y++) { const hw = 176 - y; for (let dx = -hw; dx <= hw; dx++) p.put(X + dx, y, dx === -hw ? '#6a5a42' : '#141c18'); }
  p.put(X, 170, '#141c18'); p.put(X, 171, '#141c18'); p.put(X, 169, '#6a5a42');
  return p.done();
}

/* ================= A couple on the seawall, agapanthus in the corner bed ================= */
const COUPLE = { x: 314, y: SEA };
const PERSON_A = ['..hhh..', '.hhhhh.', '.hhhhh.', '.hhhhh.', '..hhh..', '...s...', '.sssss.', 'sssssss', 'sssssss', 'sssssss', 'sssssss',
  'sssssss', '.sssss.', '.sssss.', 'sssssss', 'sssssss'];
const PERSON_B = ['.hhh...', 'hhhhh..', 'hhhhhh.', 'hhhhhh.', '.hhhh..', '..sss..', '.sssss.', 'sssssss', 'sssssss', 'sssssss', 'sssssss',
  '.sssss.', '.sssss.', 'sssssss'];
function makePerson(rows, hair, body) {               // silhouette seen from behind, rim-lit: warm on the left, cool on top
  const w = rows[0].length, h = rows.length, p = painter(w, h), on = (i, j) => i >= 0 && j >= 0 && i < w && j < h && rows[j][i] !== '.';
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    if (!on(i, j)) continue;
    let col = rows[j][i] === 'h' ? hair : body;
    if (!on(i, j - 1)) col = '#3c4466'; else if (!on(i - 1, j)) col = '#6a4a3e'; else if (!on(i + 1, j)) col = '#262a40';
    p.put(i, j, col);
  }
  return p.done();
}
// park bench, seen from behind (timber slats on cast-iron ends): its own layer, so whoever sits on it shows between the slats
function renderBench() {
  const p = painter(W, H), I = '#12111a', IH = '#34323e';
  for (const [y, lit] of [[218, 1], [219, 0], [221, 1], [222, 0]]) for (let i = BENCH.x0 + 2; i < BENCH.x1 - 1; i++) {
    const L = lampLight(i, y, 60, 40);
    p.put(i, y, lit ? dpick(['#4a3222', '#7a5234', '#aa7648'], 0.3 + L, i, y) : '#2e1e16');
  }
  for (let i = BENCH.x0 + 1; i < BENCH.x1; i++) { p.put(i, 225, '#5a3c28'); p.put(i, 226, '#2a1c16'); }
  for (const ex of [BENCH.x0, BENCH.x1 - 1]) {
    for (let y = 216; y < 237; y++) p.put(ex, y, y < 218 ? IH : I);
    for (let y = 223; y < 237; y++) p.put(ex + (ex === BENCH.x0 ? 1 : -1), y, I);
    p.put(ex + (ex === BENCH.x0 ? -1 : 1), 223, I); p.put(ex + (ex === BENCH.x0 ? -1 : 1), 236, I);
  }
  for (let y = 227; y < 236; y++) p.put(BENCH.x0 + 16, y, I);
  return p.done();
}

/* ================= Near us: fish and chips on the bench, a gull who wants some, an angler at the wall =================
   She sits behind the backrest facing the harbour, lit from the left by the park lamp, and eats a chip every 6 s
   from the paper parcel beside her; now and then she glances at the gull on the bench's end, which turns its head
   and shuffles. Further along, a man fishes over the seawall, a glow stick on his rod tip nodding with the swell;
   it twitches with a bite three times a loop, and after the last he strikes, lifting the rod, and lowers it again. */
const SITTER = { x: 112, seat: 224 };                  // her back's left column; the seat under her
const SIT_C = { hair: '#2e1c16', hairL: '#7c4c2e', top: '#33465e', topD: '#1e2836', topL: '#8a8070', skin: '#b4805c', skinD: '#6e4a36' };
const GULL = ['.hh....', 'bhkh...', '.hwggk.', '..wwggk', '...ww..', '...l...'];
const GULL_PAL = { h: '#e8ecf0', w: '#d4d8de', g: '#8a92a4', k: '#16141c', b: '#e8a040', l: '#c0563a' };
const ANGLER = { x: 376, feet: 232 };
const ANG_C = { cap: '#2a2c36', capL: '#5e6278', hair: '#2a2420', skin: '#6a4a3a', top: '#56523f', topD: '#34322a', topL: '#7a7a72', bot: '#22222c', shoe: '#141418' };
const BITES = [[44, 46.6], [119, 121.8], [181, 184]], STRIKE = [184.4, 191];
function drawSitter(t) {
  const X = SITTER.x, C = SIT_C, backH = SITTER.seat - 213, eat = frac(t / 6 + 0.3) < 0.22, glance = frac(t / 30 + 0.55) < 0.1;
  rect(ctx, X + 11, 217, 5, 8, '#d8ccb4'); rect(ctx, X + 11, 217, 5, 1, '#f2e8d2'); rect(ctx, X + 15, 218, 1, 7, '#9a9080');   // the parcel
  px(ctx, X + 12, 216, '#e8b850'); px(ctx, X + 14, 216, '#d8a040');                          // chips sticking out of it
  rect(ctx, X, 214, 7, backH, C.top); rect(ctx, X + 1, 213, 5, 1, C.topL);                      // her back, the lamp on her shoulders
  rect(ctx, X, 214, 1, backH, C.topL); rect(ctx, X + 6, 214, 1, backH, C.topD);
  rect(ctx, X - 1, 215, 1, 7, C.top); px(ctx, X - 1, 215, C.topL); rect(ctx, X + 7, 215, 1, 7, C.topD);   // arms at her sides
  if (eat) {                                                                                 // a chip to her mouth, elbow out
    px(ctx, X + 6, 212, C.topD); rect(ctx, X + 7, 213, 1, 2, C.topD); px(ctx, X + 5, 211, C.skinD); px(ctx, X + 5, 210, '#f0c860');
  }
  rect(ctx, X + 1, 209, 4, 4, C.hair); rect(ctx, X + 2, 208, 2, 1, C.hairL); rect(ctx, X + 1, 209, 1, 3, C.hairL);   // her head
  rect(ctx, X + 1, 213, 4, 2, C.hair); px(ctx, X + 1, 213, C.hairL);                         // hair to the shoulders
  if (glance) { px(ctx, X + 4, 210, C.skin); px(ctx, X + 4, 211, C.skinD); }                 // her cheek as she looks at the gull
}
function drawGull(t) {
  const look = frac(t / 5 + 0.2) < 0.45, hop = frac(t / 40 + 0.7) < 0.01 ? -1 : 0;
  ctx.drawImage(GULL_SPR[look ? 0 : 1], BENCH.x1 - 4, 210 + hop);                          // on the bench's end, facing her or the water
}
function drawAngler(t) {
  const X = ANGLER.x, F = ANGLER.feet, C = ANG_C;
  rect(ctx, X + 7, F - 4, 4, 5, '#7a8090'); rect(ctx, X + 7, F - 4, 4, 1, '#a8aebc'); rect(ctx, X + 10, F - 3, 1, 4, '#4a4e5c');   // his bucket
  rect(ctx, X - 2, F - 10, 2, 10, C.bot); rect(ctx, X + 1, F - 10, 2, 10, C.bot);           // legs and shoes
  rect(ctx, X - 2, F, 2, 1, C.shoe); rect(ctx, X + 1, F, 2, 1, C.shoe);
  rect(ctx, X - 3, F - 19, 7, 9, C.top); rect(ctx, X - 3, F - 19, 7, 1, C.topL);            // jacket, moonlit on the left
  rect(ctx, X - 3, F - 18, 1, 8, C.topL); rect(ctx, X + 3, F - 18, 1, 8, C.topD);
  rect(ctx, X + 4, F - 17, 1, 3, C.topD);                                                    // his right elbow, out to the rod
  rect(ctx, X - 2, F - 22, 4, 2, C.hair); rect(ctx, X - 1, F - 20, 2, 1, C.skin);           // the back of his head, his neck
  rect(ctx, X - 2, F - 24, 4, 2, C.cap); rect(ctx, X - 2, F - 24, 4, 1, C.capL); px(ctx, X + 2, F - 23, C.cap);   // cap
  // the rod from his hands up over the wall; the swell nods its tip, bites tug it, the strike lifts it
  let dy = Math.round(0.7 * wave(t, 40, 0.1));
  if (BITES.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 3, 91) < 0.5) dy += 2;
  const lift = t >= STRIKE[0] && t < STRIKE[1] ? Math.round(6 * smooth(STRIKE[0], STRIKE[0] + 0.5, t) * (1 - smooth(STRIKE[1] - 3, STRIKE[1], t))) : 0;
  const hx = X + 4, hy = F - 15, tx = X + 22, ty = F - 37 + dy - lift, n = Math.max(tx - hx, hy - ty);
  ctx.fillStyle = '#3a3a48';
  for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(hx, tx, k / n)), Math.round(lerp(hy, ty, k / n) + 2 * Math.sin(Math.PI * k / n) * (k / n)), 1, 1);
  ctx.globalAlpha = 0.22; ctx.fillStyle = '#a8b4cc';                                         // the line, slack down to the water
  for (let y = ty + 2; y < SEA - 8; y++) ctx.fillRect(tx + Math.round((y - ty) * 0.12), y, 1, 1);
  ctx.globalAlpha = 1;
  px(ctx, tx, ty, '#f0ff98'); px(ctx, tx, ty + 1, '#b8f060'); halo(HL.glow, tx, ty, 0.6);   // the glow stick
}
function drawCouple(t) {
  const X = COUPLE.x, Y = COUPLE.y;
  ctx.drawImage(PEOPLE[0], X, Y - 16);
  const lean = wave(t, 3, 0.2) > -0.6 ? 1 : 0;                                            // B rests a head on A's shoulder
  ctx.drawImage(PEOPLE[1], 0, 5, 7, 9, X + 7, Y - 9, 7, 9);
  ctx.drawImage(PEOPLE[1], 0, 0, 7, 5, X + 7 - lean, Y - 14 + lean, 7, 5);
  if (t >= 100 && t < 114) {                                                              // A holds up a phone to film the storm
    const up = smooth(100, 101.2, t) * (1 - smooth(112.6, 114, t)), yy = Math.round(Y - 14 - up * 8);
    rect(ctx, X + 5, yy + 2, 1, Math.round(up * 8), '#100e18');
    rect(ctx, X + 5, yy - 1, 2, 3, '#bcdcff'); px(ctx, X + 5, yy - 1, '#ffffff');
    halo(HL.phone, X + 6, yy, 0.7 * up);
  }
}
const AGAP = [{ x: 434, h: 24, k: 13, ph: 0.1 }, { x: 447, h: 30, k: 11, ph: 0.5 }, { x: 459, h: 26, k: 15, ph: 0.8 }, { x: 471, h: 32, k: 12, ph: 0.3 }];
function renderAgapLeaves() {
  const p = painter(W, H), r = rng(929);
  for (let k = 0; k < 34; k++) {                                   // strappy leaves arching out of the bed
    const bx = 420 + r() * 66, len = 10 + r() * 14, dir = r() < 0.5 ? -1 : 1, lean = 0.4 + r() * 0.8;
    for (let s = 0; s <= len; s++) {
      const u = s / len, x = Math.round(bx + dir * lean * s * 0.8), y = Math.round(H + 2 - s * 1.1 + u * u * len * 0.9);
      p.put(x, y, u > 0.7 ? '#1e3a26' : u > 0.3 ? '#16301e' : '#0e2016');
    }
  }
  return p.done();
}
function renderAgapHead() {                          // a round umbel of little blue-violet trumpets
  const p = painter(11, 11);
  for (let j = 0; j < 11; j++) for (let i = 0; i < 11; i++) {
    const d = Math.hypot(i - 5, j - 5), n = hash(i, j, 31);
    if (d > 4.4 + (n - 0.5) * 1.6) continue;
    const v = 0.85 - (j - 1) / 12 - (i - 3) / 20 + (n - 0.5) * 0.35;
    p.put(i, j, dpick(['#3a4290', '#4e58b0', '#6a76d4', '#8a96f0', '#b0baff'], v, i, j));
  }
  return p.done();
}
function drawAgapanthus(t) {
  for (const a of AGAP) {
    const dx = wave(t, a.k, a.ph) * 1.4, top = H - a.h;
    ctx.fillStyle = '#1e3a26';
    for (let y = top + 3; y < H; y++) ctx.fillRect(Math.round(a.x + dx * (H - y) / a.h), y, 1, 1);
    ctx.drawImage(AGAP_HEAD, Math.round(a.x + dx) - 5, top - 5);
  }
}

/* ================= The jacaranda: trunk, arching limbs, a canopy of violet bells ================= */
const LIMBS = [
  { p: [46, 276, 55, 206, 38, 152], w: [12, 8] },                // trunk
  { p: [37, 154, 20, 112, 8, 56], w: [6, 3] },                   // left leader, framing the side
  { p: [8, 58, 2, 34, -6, 16], w: [3, 2] },
  { p: [39, 156, 70, 110, 150, 72], w: [6, 3] },                 // the long arching limb
  { p: [150, 72, 204, 50, 268, 30], w: [3, 1.5] },
  { p: [40, 146, 42, 86, 80, 38], w: [5, 2] },
  { p: [80, 38, 112, 16, 176, 8], w: [2, 1] },
  { p: [106, 95, 140, 86, 204, 82], w: [2.5, 1] },               // a low bough (the possum's walk)
  { p: [22, 104, 6, 84, -4, 80], w: [3, 1.5] },
  { p: [210, 48, 250, 30, 302, 14], w: [1.5, 1] },
  { p: [62, 64, 60, 32, 40, 8], w: [2.5, 1] },
  { p: [40, 276, 18, 228, 12, 150], w: [7, 3] },                 // a second stem leaning out to the left
];
const bez = (P, s) => { const a = 1 - s; return [a * a * P[0] + 2 * a * s * P[2] + s * s * P[4], a * a * P[1] + 2 * a * s * P[3] + s * s * P[5]]; };
const CAN_H = 172;
const BLOBS = [];
{
  const r = rng(4040);
  for (let k = 0; k < 170; k++) {                                // the upper band, heavy on the left, thinning to the right
    const x = -12 + Math.pow(r(), 1.2) * 322, e = 56 - x * 0.15;
    BLOBS.push({ x, y: -6 + r() * (e + 4), rx: 6 + r() * 8 - x * 0.01, ry: 4 + r() * 4 });
  }
  for (let k = 0; k < 64; k++) {                                 // the mass hanging down the left side
    const y = 40 + Math.pow(r(), 1.1) * 124, x = -8 + r() * (42 - (y - 40) * 0.14);
    BLOBS.push({ x, y, rx: 5 + r() * 6, ry: 4 + r() * 4 });
  }
  for (let k = 0; k < 46; k++) {                                 // small clumps breaking up the band's lower edge
    const x = 36 + r() * 270, e = 56 - x * 0.15;
    BLOBS.push({ x, y: e + (r() - 0.3) * 8, rx: 2.5 + r() * 3, ry: 2 + r() * 2.5 });
  }
  for (let i = BLOBS.length - 1; i >= 0; i--) {                  // keep Sydney Tower's turret in view
    const b = BLOBS[i];
    if (b.x + b.rx > SYT.x - 9 && b.x - b.rx < SYT.x + 9 && b.y + b.ry > 40) BLOBS.splice(i, 1);
  }
  LIMBS.forEach((L, li) => {                                     // clusters along the outer parts of the limbs
    if (li === 0) return;
    for (let s = li === 3 || li === 7 ? 0.8 : 0.4; s <= 1; s += 0.1) {
      const [x, y] = bez(L.p, s);
      if (r() < 0.7) BLOBS.push({ x: x + (r() - 0.5) * 8, y: y - 2 - r() * 5, rx: 3.5 + r() * 4.5, ry: 3 + r() * 2.5 });
    }
  });
}
const CAN = ['#150e26', '#1f1538', '#2a1d4c', '#372762', '#463278', '#573e90', '#6a4ea8', '#8064be', '#987ed2', '#b49ae4'];
const CAN_W = ['#24162e', '#361f42', '#4c2a56', '#66386a', '#82487c', '#a05c8e', '#bc76a2', '#d496b8'];
const CLUSTERS = [];
function renderTree() {
  const p = painter(W, H), D = new Float32Array(W * CAN_H).fill(-9);
  // limbs and twigs (the canopy covers most of them)
  const r = rng(5151);
  LIMBS.forEach((L, li) => {
    const n = Math.ceil(Math.hypot(L.p[4] - L.p[0], L.p[5] - L.p[1]) * 1.6);
    for (let k = 0; k <= n; k++) {
      const s = k / n, [cx, cy] = bez(L.p, s), rad = lerp(L.w[0], L.w[1], s) / 2;
      const lampK = Math.max(0, 1 - Math.hypot(cx - LAMP.x, cy - LAMP.y) / 130);
      for (let dy = -Math.ceil(rad); dy <= Math.ceil(rad); dy++) for (let dx = -Math.ceil(rad); dx <= Math.ceil(rad); dx++) {
        if (dx * dx + dy * dy > rad * rad + 0.5) continue;
        const X = Math.round(cx + dx), Y = Math.round(cy + dy), nx = dx / Math.max(0.5, rad);
        let col = hash(Math.round(cx * 0.5 + dx), Y >> 2, li) < 0.2 ? '#231b22' : li === 0 ? '#3a2e32' : '#2e242a';
        if (nx > 0.3) col = lampK > 0.3 ? (lampK > 0.55 && nx > 0.6 ? '#9a7658' : '#6e5244') : '#3e3440';
        else if (nx < -0.55 || dy < -rad * 0.6) col = '#4a4256';
        p.put(X, Y, col);
      }
      if (li > 0 && k % 7 === 3 && r() < 0.6) {                  // twigs
        const a = -Math.PI / 2 + (r() - 0.5) * 2.2, len = 4 + r() * 8;
        for (let m = 1; m <= len; m++) p.put(Math.round(cx + Math.cos(a) * m), Math.round(cy + Math.sin(a) * m + m * m * 0.02), '#1c161e');
      }
    }
  });
  for (let dx = -10; dx <= 10; dx++) for (let y = 258; y < H; y++) if (Math.abs(dx) < 5 + (y - 258) * 0.8)
    p.put(46 + dx, y, dx > 3 ? '#7a5a48' : dx > 0 ? '#4e3c38' : hash(dx, y >> 1, 9) < 0.3 ? '#231b22' : '#3a2e32');   // root flare
  // canopy density: elliptical clumps with lobed, lacy edges
  for (const b of BLOBS) {
    for (let y = Math.max(0, Math.floor(b.y - b.ry - 1)); y <= Math.min(CAN_H - 1, Math.ceil(b.y + b.ry + 1)); y++)
      for (let x = Math.max(0, Math.floor(b.x - b.rx - 1)); x <= Math.min(W - 1, Math.ceil(b.x + b.rx + 1)); x++) {
        const dx = (x - b.x) / b.rx, dy = (y - b.y) / b.ry, v = 1 - dx * dx - dy * dy, o = y * W + x;
        if (v > D[o]) D[o] = v;
      }
  }
  const nz = (x, y) => 0.18 * Math.sin(x * 0.9 + y * 0.35) * Math.sin(y * 0.8 - x * 0.25) + (hash(x >> 1, y >> 1, 21) - 0.5) * 0.22;
  const inC = (x, y) => x >= 0 && y >= 0 && x < W && y < CAN_H && D[y * W + x] + nz(x, y) > 0;
  for (let x = 0; x < W; x++) {
    let depth = 0;
    for (let y = 0; y < CAN_H; y++) {
      const v0 = D[y * W + x] + nz(x, y);
      if (v0 <= 0) { depth = 0; continue; }
      if (v0 < 0.07 && hash(x, y, 23) < 0.35) { depth = 0; continue; }                   // lace: tiny gaps at the edges
      depth = Math.min(depth + 1, 7);
      const under = !inC(x, y + 1) || !inC(x, y + 2);
      const clump = 0.6 * hash(x >> 1, y >> 1, 77) + 0.4 * hash((x + 1) >> 2, (y + 2) >> 2, 78);
      let v = 0.8 - depth * 0.075 + (clump - 0.5) * 0.3 - (x < 30 ? 0.08 : 0);
      if (under) v = 0.26 + (clump - 0.5) * 0.2;
      const lampK = Math.pow(clamp(1 - Math.hypot((x - LAMP.x) / 120, (y - LAMP.y) / 150), 0, 1), 1.4);
      let col;
      if (lampK * (under ? 1.3 : 0.7) > bayer(x, y) * 0.9 + 0.05) col = dpick(CAN_W, v + lampK * 0.45, x, y);
      else col = dpick(CAN, v, x, y);
      if (depth <= 2 && hash(x, y, 24) < 0.045) col = '#c8b0f4';                          // single bells catching the light
      else if (depth >= 3 && hash(x, y, 25) < 0.025) col = '#1c3024';                      // a few ferny leaves
      p.put(x, y, col);
    }
  }
  // hanging clusters along the lower edge (drawn per frame, swaying)
  CLUSTERS.length = 0;
  const rc = rng(6262);
  const addCluster = (x, y) => {
    const w = 9, h = 13, cp = painter(w, h), rx = 2.6 + rc() * 1.2, ry = 2.2 + rc() * 1, stem = 2 + Math.floor(rc() * 2);
    for (let j = 0; j < stem; j++) cp.put(4, j, '#231a2a');
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
      const dx = (i - 4) / rx, dy = (j - stem - ry) / ry, dx2 = (i - 4) / (rx * 0.62), dy2 = (j - stem - ry * 2.3) / (ry * 0.9);
      const v = Math.max(1 - dx * dx - dy * dy, 1 - dx2 * dx2 - dy2 * dy2) + (hash(i, j, CLUSTERS.length) - 0.5) * 0.5;
      if (v <= 0) continue;
      const lampK = Math.max(0, 1 - Math.hypot(x - LAMP.x, y - LAMP.y) / 150);
      const q = 0.78 - (j - stem) / (ry * 3.2) * 0.6 + (hash(i, j, 90) - 0.5) * 0.25 - (i > 5 ? 0.12 : 0);
      cp.put(i, j, lampK > 0.3 && j > stem + ry ? dpick(CAN_W, q + lampK * 0.4, i, j) : dpick(CAN, q, i, j));
    }
    CLUSTERS.push({ x: x - 4, y, c: cp.done(), k: 9 + Math.floor(rc() * 9), ph: rc(), k2: 29 + Math.floor(rc() * 20) });
  };
  for (let x = 44 + Math.floor(rc() * 5); x < 300; x += 10 + Math.floor(rc() * 6)) {
    let yb = -1;
    for (let y = CAN_H - 1; y >= 0; y--) if (inC(x, y)) { yb = y; break; }
    if (yb > 0 && yb < 70 && Math.abs(x - SYT.x) > 10) addCluster(x, yb - 1);
  }
  for (let y = 70; y < 150; y += 14 + Math.floor(rc() * 8)) {
    let xr = -1;
    for (let x = 60; x >= 0; x--) if (inC(x, y)) { xr = x; break; }
    if (xr > 10) addCluster(xr + 2, y);
  }
  return p.done();
}
function drawClusters(t) {
  for (const c of CLUSTERS) ctx.drawImage(c.c, c.x + Math.round(1.1 * wave(t, c.k, c.ph) + 0.4 * wave(t, c.k2, c.ph * 2)), c.y);
}

/* ================= A brushtail possum on the low bough ================= */
const POSSUM = {
  side: ['..........e.e.', '.........eHeH.', '....BBBBBHHHHH', '..BBBBBBBHHEHn', '.bbbbbbbbbHHH.', 'tbbbbbbbbbbw..', 'tbbwbbbbbw....',
    'tt............', '.tt...........', '..tt..........', '...t..........'],
  walk: ['..........e.e.', '.........eHeH.', '....BBBBBHHHHH', '..BBBBBBBHHEHn', '.bbbbbbbbbHHH.', 'tbbbbbbbbbbbw.', 'tbwbbbbbbbw...',
    'tt............', '.tt...........', '..tt..........', '...t..........'],
  face: ['.........e..e.', '.........eHHe.', '....BBBBBHHHH.', '..BBBBBBBEHEH.', '.bbbbbbbbbHnH.', 'tbbbbbbbbbbw..', 'tbbwbbbbbw....',
    'tt............', '.tt...........', '..tt..........', '...t..........'],
};
const POSSUM_PAL = { b: '#6a6474', B: '#9a929e', H: '#8a8490', e: '#b09098', E: '#eaffc8', n: '#e0a0a0', t: '#141018', w: '#b0aab4' };
const POSSUM_KEYS = [[0, 140, 1], [46, 140, 1], [53, 162, 1], [118, 162, 1], [119, 162, -1], [126, 140, -1], [230, 140, -1], [231, 140, 1], [240, 140, 1]];
const POSSUM_LOOKS = [[68, 75], [100, 105], [150, 157], [196, 201]];
const BOUGH_TOP = new Int16Array(W).fill(0);
function possumState(t) {
  for (let i = 0; i < POSSUM_KEYS.length - 1; i++) {
    const [t0, x0, d0] = POSSUM_KEYS[i], [t1, x1] = POSSUM_KEYS[i + 1];
    if (t >= t0 && t < t1) return { x: Math.round(lerp(x0, x1, (t - t0) / (t1 - t0))), dir: d0, moving: x0 !== x1 };
  }
  return { x: 140, dir: 1, moving: false };
}
function drawPossum(t) {
  const s = possumState(t), look = !s.moving && POSSUM_LOOKS.some(([a, b]) => t >= a && t < b);
  const kind = s.moving ? (step(t, 1920) & 1 ? 'walk' : 'side') : look ? 'face' : 'side';
  const spr = POSSUM_SPR[kind][s.dir > 0 ? 0 : 1], y = BOUGH_TOP[s.x] - 7;
  ctx.drawImage(spr, s.x - 7, y);
  const tail = Math.round(wave(t, 20, 0.3) * 0.8);                                        // the tail sways a little
  px(ctx, s.x - 7 + (s.dir > 0 ? 3 + tail : 10 - tail), y + 11, '#141018');
  if (look) halo(HL.eye, s.x + (s.dir > 0 ? 3 : -4), y + 3, 0.9);
  else halo(HL.eye, s.x + (s.dir > 0 ? 4 : -5), y + 3, 0.4);
}

/* ================= Falling jacaranda bells (3 depths) ================= */
const BELL_COLS = [
  { L: '#b8a0f0', M: '#8a6ad4', D: '#5a42a0', T: '#ece4ff' },
  { L: '#a890e8', M: '#7a5cc8', D: '#4e3890', T: '#e0d6ff' },
  { L: '#c8b0f6', M: '#9c7ce0', D: '#6a4cb4', T: '#f4eeff' },
];
const BELL_SHAPES = [
  null,
  [['.L.', 'LMD', 'M.D'], ['.M.', 'MMD', 'D.D']],
  [['.LM..', '.LMM.', 'LLMMD', 'LMTMD', 'M.D.D'], ['.MD..', '.MMD.', 'MMMDD', 'MDTDD', 'D.D.D']],
];
const BELLS = [[], [], []];
{
  const r = rng(4242);
  [[40, 5, 8, 3, 24], [24, 7, 10, 6, 46], [11, 9, 13, 11, 80]].forEach(([count, nMin, nMax, amp, drift], size) => {
    for (let i = 0; i < count; i++) BELLS[size].push({
      id: size * 100 + i, size, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 30 + Math.floor(r() * 30), p2: r(), kr: 14 + Math.floor(r() * 20), p3: r(), kf: 40 + Math.floor(r() * 40), p4: r(),
      amp: amp * (0.7 + r() * 0.6), drift: drift * (0.6 + r() * 0.8), c: Math.floor(r() * 3),
    });
  });
}
const BELL_Y = [[20, 236], [12, 262], [-10, 282]];
function drawBell(b, t) {
  const { cyc, u } = cycle(t, b.n, b.ph);
  const sw = wave(t, b.ks, b.p2), [y0, y1] = BELL_Y[b.size];
  const x = -10 + Math.pow(hash(b.id, cyc, 5), 1.3) * 340 + u * b.drift + sw * b.amp;
  const y = y0 + u * (y1 - y0) - Math.abs(sw) * (b.size + 1);
  if (x < -8 || x > W + 8) return;
  const col = BELL_COLS[(b.c + cyc) % 3];
  if (b.size === 0) { ctx.fillStyle = hash(b.id, cyc, 6) < 0.5 ? col.M : col.L; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); return; }
  const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p4)), spr = BELL_SPR[(b.c + cyc) % 3][b.size][fl < 0 ? 1 : 0];
  if (b.size === 1) { ctx.drawImage(spr, Math.round(x) - 1, Math.round(y) - 1); return; }
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.rotate(sw * 0.8 + 0.4 * wave(t, b.kr, b.p3));
  ctx.drawImage(spr, -2, -2);
  ctx.restore();
}
// a picked weather (site/js/engine/weather.js): the bells fall only with Sydney's own weather; far drops end by the far
// shore (behind the Opera House), mid ones on the harbour before the seawall, near ones fall past the tree and out of
// the bottom, catching the park lamp, the floodlit Opera House, the bridge pylons and Sydney Tower's turret
const WEATHER = weatherPlans(['blossoms']);
const WX_LAND = [[HZ - 2, HZ + 12], [SHORE + 1, SEA - 2], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LAMP.x, y: LAMP.y + 6, rx: 40, ry: 34 },
  { x: SYT.x, y: 56, rx: 12, ry: 10, col: '#ffd890' },
  ...[BR.s0, BR.n0].map(x => ({ x: x + 7, y: 146, rx: 14, ry: 18, col: '#ffcf8a' })),
  { x: FOCUS, y: 158, rx: 64, ry: 26, col: '#ffe6c0' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 30, ry: 16, col: '#ffc0a0' }] };   // + the screen

/* ================= Flying foxes crossing the sky ================= */
const BAT_ROWS = [
  [['X.....X', '.X.B.X.', '...B...'], ['.......', 'XXXBXXX', '...B...'], ['.......', '.XXBXX.', 'X..B..X']],
  [['X.........X', '.XX.....XX.', '...XXBXX...', '....BBB....', '.....B.....'],
    ['...........', '...........', 'XXXXXBXXXXX', '.X.XBBBX.X.', '.....B.....'],
    ['...........', '...........', '...XXBXX...', '.XXXBBBXXX.', 'X.X..B..X.X']],
  [['X.......................X', 'XX.....................XX', '.XXX.................XXX.', '.XXXXX.............XXXXX.', '..XXXXXX.........XXXXXX..',
    '..X.XXXXXX..C..XXXXXX.X..', '.......XXXXXBXXXXX.......', '...........BBB...........', '............B............'],
   ['.........................', '.........................', '.........................', '.........................', '.........................',
    'XXXXXXXXXXXXCXXXXXXXXXXXX', '.XXXXXXXXXXXBXXXXXXXXXXX.', '..X.X.X.X..BBB..X.X.X.X..', '............B............'],
   ['.........................', '.........................', '.........................', '.........................', '..........XXCXX..........',
    '.....XXXXXXXBXXXXXXX.....', '..XXXXXXXXXBBBXXXXXXXXX..', '.XXXX.X.X...B...X.X.XXXX.', 'XX.....................XX']],
];
const BAT_PAL = { X: '#3a2c32', B: '#624842', C: '#8e5632' };
const BAT_GROUPS = [
  { a: 8, b: 46, x0: 500, x1: 236, y0: 44, y1: 14, size: 0, bats: [[0, 0], [9, -5], [15, 4], [24, -2]] },            // into the canopy
  { a: 92, b: 128, x0: 280, x1: 512, y0: 14, y1: 64, size: 1, bats: [[0, 0], [-12, 6], [-20, -4], [-30, 3], [-38, -6]] },  // out of it, past the moon
  { a: 165, b: 205, x0: 500, x1: 250, y0: 26, y1: 10, size: 0, bats: [[0, 0], [8, 5], [18, -2]] },
];
const NEAR_BAT = { a: 128, b: 136.5 };
function batFrame(t, ph) { return [0, 1, 2, 1][Math.floor(t * 9 + ph * 4) & 3]; }
function drawBatGroup(g, t) {
  if (t < g.a || t >= g.b) return;
  const u = (t - g.a) / (g.b - g.a), cx = lerp(g.x0, g.x1, u), cy = lerp(g.y0, g.y1, u);
  g.bats.forEach(([ox, oy], i) => {
    const bob = wave(t, 37 + i * 5, i * 0.21) * 2, spr = BAT_SPR[g.size][batFrame(t, i * 0.37)];
    const x = Math.round(cx + ox + wave(t, 11 + i, i * 0.3) * 3), y = Math.round(cy + oy + bob);
    ctx.drawImage(spr, x - (spr.width >> 1), y - (spr.height >> 1));
  });
}
function drawNearBat(t) {
  if (t < NEAR_BAT.a || t >= NEAR_BAT.b) return;
  const u = (t - NEAR_BAT.a) / (NEAR_BAT.b - NEAR_BAT.a), x = Math.round(lerp(512, -34, u)), y = Math.round(lerp(58, 22, u) - 14 * Math.sin(Math.PI * u));
  const spr = BAT_SPR[2][u > 0.3 && u < 0.5 ? 1 : batFrame(t, 0.2)];
  ctx.drawImage(spr, x - 12, y - 4);
}

/* ================= Small things: moths at the lamp, a plane, a shooting star ================= */
const MOTHS = [{ k: 70, ph: 0.1, rx: 8, ry: 6 }, { k: 83, ph: 0.55, rx: 11, ry: 7 }, { k: 61, ph: 0.8, rx: 6, ry: 9 }];
function drawMoths(t) {
  const flap = step(t, 2400) & 1;
  MOTHS.forEach((m, i) => {
    const a = TAU * (m.k * t / LOOP + m.ph);
    const x = LAMP.x + Math.cos(a) * m.rx + Math.sin(a * 3 + i) * 1.5, y = LAMP.y - 2 + Math.sin(a) * m.ry + Math.cos(a * 2 + i) * 1.2;
    halo(HL.moth, x, y, 0.8);
    px(ctx, Math.round(x), Math.round(y), '#fff2dc');
    if (flap) px(ctx, Math.round(x) + 1, Math.round(y), '#d8cab0');
  });
}
function drawPlane(t) {
  if (t < 50 || t >= 100) return;
  const u = (t - 50) / 50, x = Math.round(lerp(490, 250, u)), y = Math.round(lerp(18, 30, u));
  rect(ctx, x - 1, y, 3, 1, '#2a2a3a');
  if (frac(t / 1.3) < 0.1) { px(ctx, x + 1, y, '#ffffff'); halo(HL.strobe, x + 1, y, 0.8); }
  if (frac(t / 1.6 + 0.3) < 0.5) px(ctx, x - 1, y - 1, '#ff4040');
}
function drawShootingStar(t) {
  const u = (t - 158.3) / 0.9;
  if (u < 0 || u > 1) return;
  const a = Math.sin(Math.PI * u);
  for (let k = 0; k < 14; k++) {
    const uu = u - k * 0.02;
    if (uu < 0) break;
    ctx.globalAlpha = a * (1 - k / 14);
    px(ctx, Math.round(372 + 60 * uu), Math.round(10 + 22 * uu), k < 2 ? '#ffffff' : '#c8dcff');
  }
  ctx.globalAlpha = 1;
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 4; d[o + 2] = 20; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let AGAP_HEAD, PEOPLE, OH_R, POINT_L, BENCH_L, GULL_SPR, HL, SKY_C, MOON_C, STORM_C, STORM_LIT, BOLTS, NORTH, CITY, BRIDGE, WATER_C, FERRY_SPR, TAXI_SPR, OH_L, GROUND_L,
  AGAP_L, TREE_L, BELL_SPR, BAT_SPR, POSSUM_SPR, VIG_C, BILL_L, AD_C, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(40, '#8a90c8', 0.2, 1.8), moonCore: makeHalo(14, '#e8e4d0', 0.28, 1.5),
    flash: makeHalo(100, '#7a72c8', 0.26, 1.4), flashW: makeHalo(46, '#8a82d0', 0.16, 1.4), strobe: makeHalo(5, '#ffffff', 0.6, 1),
    red: makeHalo(4, '#ff3030', 0.6, 1), redL: makeHalo(5, '#ff4a3a', 0.6, 1), green: makeHalo(5, '#5aff8a', 0.6, 1),
    syt: makeHalo(24, '#ffc860', 0.22, 1.5), sytCore: makeHalo(9, '#fff0c0', 0.35, 1.3), crown: makeHalo(7, '#e8f0ff', 0.25),
    head: makeHalo(3, '#fff0c0', 0.35, 1), train: makeHalo(12, '#ffe0a0', 0.2), trainHead: makeHalo(6, '#ffffff', 0.5, 1.2),
    spark: makeHalo(7, '#c8e0ff', 0.7, 1), archLight: makeHalo(3, '#fff0d0', 0.5, 1), torch: makeHalo(3, '#9adcff', 0.6, 1),
    pylon: makeHalo(26, '#ffcf8a', 0.16, 1.6),
    oh: makeHalo(86, '#ffd8a0', 0.13, 1.6), ohCore: makeHalo(30, '#fff0d0', 0.14, 1.4), ohLamp: makeHalo(4, '#ffe0a0', 0.45, 1),
    ferry: makeHalo(24, '#ffd8a0', 0.16), mast: makeHalo(4, '#ffffff', 0.5, 1), taxi: makeHalo(9, '#ffe8b0', 0.3),
    lamp: makeHalo(64, '#ffb866', 0.24, 1.7), lampCore: makeHalo(16, '#ffe6b0', 0.5, 1.4), moth: makeHalo(2, '#fff0c0', 0.45, 1),
    phone: makeHalo(8, '#a8d0ff', 0.4, 1.2), eye: makeHalo(3, '#e8ffc0', 0.6, 1), glow: makeHalo(5, '#c8ff70', 0.45, 1.2),
    bbGlow: makeHalo(34, '#ffc0a0', 0.15, 1.6), bbLed: makeHalo(3, '#ff4a3a', 0.5, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  const D = stormField();
  STORM_C = renderStorm(D, 0); STORM_LIT = [1, 2, 3].map(k => renderStorm(D, k)); BOLTS = [renderBolt(11), renderBolt(12), renderBolt(13), renderBolt(14, true)];
  NORTH = renderNorth(); CITY = renderCity(); BRIDGE = renderBridge(); WATER_C = renderWater();
  BILL_L = trim(renderBillboard()); AD_C = renderAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#a38bff' });
  FERRY_SPR = [makeFerry(true), makeFerry(false)];
  const taxi = sprite(TAXI_ROWS, TAXI_PAL); TAXI_SPR = [taxi, flipH(taxi)];
  OH_L = trim(renderOperaHouse(false)); OH_R = renderOperaMirror(renderOperaHouse(true)); POINT_L = trim(renderPoint());
  GROUND_L = trim(renderGround()); BENCH_L = trim(renderBench()); AGAP_L = trim(renderAgapLeaves()); TREE_L = trim(renderTree());
  const bough = LIMBS[7];
  for (let k = 0; k <= 200; k++) {
    const s = k / 200, [cx, cy] = bez(bough.p, s), x = Math.round(cx), top = Math.round(cy - lerp(bough.w[0], bough.w[1], s) / 2);
    if (x >= 0 && x < W && (!BOUGH_TOP[x] || top < BOUGH_TOP[x])) BOUGH_TOP[x] = top;
  }
  BELL_SPR = BELL_COLS.map(col => BELL_SHAPES.map(sh => sh && sh.map(rows => sprite(rows, col))));
  BAT_SPR = BAT_ROWS.map(fr => fr.map(rows => sprite(rows, BAT_PAL)));
  POSSUM_SPR = {};
  for (const k in POSSUM) POSSUM_SPR[k] = [sprite(POSSUM[k], POSSUM_PAL), sprite(POSSUM[k], POSSUM_PAL, true)];
  AGAP_HEAD = renderAgapHead();
  PEOPLE = [makePerson(PERSON_A, '#16121a', '#100e18'), makePerson(PERSON_B, '#1e1418', '#121018')];
  GULL_SPR = [sprite(GULL, GULL_PAL), sprite(GULL, GULL_PAL, true)];
  VIG_C = renderVignette();
}

/* ================= Per-frame: sky, storm, skyline ================= */
function drawSky(t, fl) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.35; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 1;
  drawShootingStar(t);
  halo(HL.moon, MOON.x, MOON.y, 0.9); halo(HL.moonCore, MOON.x - 3, MOON.y, 0.8);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawPlane(t);
  ctx.drawImage(STORM_C, STORM.x0, STORM.y0);
  if (fl && fl.I > 0.01) {
    const f = FLASH_C[fl.f.c - 1];
    ctx.globalAlpha = Math.min(1, fl.I * 1.1); ctx.drawImage(STORM_LIT[fl.f.c - 1], STORM.x0, STORM.y0); ctx.globalAlpha = 1;
    halo(HL.flash, f.x, f.y, fl.I);
    if (fl.f.big && fl.dt >= 0.3 && fl.dt < 0.44) {
      ctx.globalAlpha = 1 - (fl.dt - 0.3) / 0.16;
      ctx.drawImage(BOLTS[fl.f.c - 1], f.bx, f.by);
      if (fl.f.c === 2) ctx.drawImage(BOLTS[3], 464, 118);                                  // and a far strike to the sea
      ctx.globalAlpha = 1;
    }
  }
}
function winOn(w, salt, t) { return hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p; }
function drawSkyline(t) {
  blit(NORTH.L);
  for (const w of NORTH.wins) if (winOn(w, 500, t)) px(ctx, w.x, w.y, w.col);
  for (const l of NORTH.lights) { ctx.globalAlpha = 0.6 + 0.4 * wave(t, l.k, l.ph); px(ctx, l.x, l.y, l.col); }
  ctx.globalAlpha = 1;
  halo(HL.syt, SYT.x, 56, 0.9 + 0.1 * wave(t, 6));
  blit(CITY.L);
  for (const w of CITY.wins) if (winOn(w, 900, t)) px(ctx, w.x, w.y, w.col);
  for (const c of CITY.crowns) { rect(ctx, c.x, c.y, c.w, 1, c.col); halo(HL.crown, c.x + (c.w >> 1), c.y, 0.7 + 0.2 * wave(t, 9, c.x / 97)); }
  halo(HL.sytCore, SYT.x, 56, 0.8);
  const blink = frac(t / 1.5) < 0.5;
  [...CITY.beacons, ...NORTH.beacons].forEach(([bx, by], i) => {
    if (frac(t / 1.5 + i * 0.29) < 0.5 || (i === CITY.beacons.length - 1 && blink)) { px(ctx, bx, by, '#ff4040'); halo(HL.red, bx, by, 0.75); }
  });
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves it out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), fl = wx.blossoms && !reducedMotion ? flashAt(t) : null, wxc = billboard ? WX_BB : WX;
  drawSky(t, fl);
  drawBatGroup(BAT_GROUPS[0], t); drawBatGroup(BAT_GROUPS[2], t);
  drawSkyline(t);
  if (billboard) drawBillboard(t, pitch);
  blit(BRIDGE.body);
  drawTraffic(t);
  blit(BRIDGE.front);
  drawBridgeLights(t);
  drawWater(t, fl);
  drawFerry(FERRIES[1], t); drawFerry(FERRIES[2], t);
  if (wx.blossoms) for (const b of BELLS[0]) drawBell(b, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawOperaHouse(t);
  drawMarkers(t);
  drawFerry(FERRIES[0], t);
  for (const tx of TAXIS) drawTaxi(tx, t);
  drawBatGroup(BAT_GROUPS[1], t);
  if (wx.blossoms) for (const b of BELLS[1]) drawBell(b, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  blit(GROUND_L);
  drawCouple(t);
  drawSitter(t); blit(BENCH_L); drawGull(t);
  drawAngler(t);
  blit(AGAP_L); drawAgapanthus(t);
  blit(TREE_L);
  drawClusters(t);
  drawPossum(t);
  let li = 0.94 + 0.06 * wave(t, 1500, 0.3);
  if ((t >= 83 && t < 84.5) || (t >= 197.2 && t < 198.4)) { const hv = hash(7, Math.floor(t * 20)); li = hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.12; }
  if (li < 0.9) rect(ctx, LAMP.x - 2, 177, 5, 10, `rgba(40,20,8,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y, li);
  drawMoths(t);
  drawNearBat(t);
  if (wx.blossoms) for (const b of BELLS[2]) drawBell(b, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'sydney',
  name: 'Sydney',
  tagline: 'Jacaranda night on the harbour',
  timeZone: 'Australia/Sydney',
  accent: '#a38bff',
  focusX: FOCUS,
  landmark: [OH.x0, OH.x1],                            // the Opera House
  landmarkTop: 104,                                    // the row of the highest sail, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.4, crickets: 0.45, wind: 0.2, traffic: 0.12 },
  events: [
    ...FLASHES.map(f => ({ t: +(f.t + 1).toFixed(1), type: 'thunder', x: FLASH_C[f.c - 1].x, gain: f.big ? 0.75 : 0.4 })),
    { t: 18, type: 'horn', x: 20 }, { t: 119.5, type: 'horn', x: 470, gain: 0.6 }, { t: 188, type: 'horn', x: 230, gain: 0.5 },
    { t: 36, type: 'train', dur: 44, pan: [-0.2, 1] }, { t: 146, type: 'train', dur: 44, pan: [1, -0.2] },
    { t: 56, type: 'boat', dur: 32, pan: [1, -1], gain: 0.5 }, { t: 150, type: 'boat', dur: 30, pan: [-1, 1], gain: 0.5 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
